// Explore — buyer's default landing page.
// Faithful port of the consumer Explore design (handoff): hero + new launches +
// hottest + picked-for-you + dispatches, with five sub-views (LaunchesAll,
// HotChart, Newsroom, Property, Article) managed via internal view state.
// Buyer prefs are edited exclusively from the workspace surface
// (PrefsEditModal); Explore reads from the workspace and never writes to it.

import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { adaptBuyerCard, articlesApiEnabled, listNews, type ArticleWire, type Section } from "@/lib/articlesApi";
import {
  COSTS_DEFAULT_SAMPLE_PRICE,
  COSTS_DOWN_PAYMENT_RATE,
  COSTS_LTV_RATE,
  COSTS_STRESS_RATE,
  COSTS_LOAN_TERM_YEARS,
} from "@/constants/costs";
import {
  adaptHottestRow,
  adaptPickedRow,
  adaptPipelineRow,
  adaptProjectDetails,
  bucketTimeOfDayClientFallback,
  exploreApiEnabled,
  getGreeting,
  getProjectDetails,
  getProjectFloorplan,
  GREETING_FALLBACK_HEADLINE,
  listHottest,
  listPicked,
  listPipeline,
  listProjectAssets,
  resolveSamplePrice,
  sortHotChart,
  toneFromName,
  type AssetRowWire,
  type GreetingResp,
  type PlanTypeWire,
  type PropertyDetail,
  type SamplePriceResult,
} from "@/lib/exploreApi";
import { useCurrentWorkspace } from "@/hooks/useCurrentWorkspace";
import { PropertyReviews, ReviewBadge } from "@/components/PropertyReviews";
import { ProjectReportButton } from "@/components/ProjectReportButton";
import { LeadCta } from "@/components/LeadCta";
import { estBsd } from "@/constants/calc";

/* ═════════════════════════════════════════════════════════════
   MOBILE OVERRIDES — desktop layout is preserved by leaving the
   inline styles untouched; mobile-only @media rules below
   (max-width: 767px) override grid templates, paddings, and a
   handful of font sizes. Scoped to `.pw-explore` so nothing leaks.
   ═════════════════════════════════════════════════════════════ */
const MOBILE_CSS = `
@media (max-width: 767px) {
  /* Horizontal padding 32 → 16 (top bars, sections, headers, article body) */
  .pw-explore .pw-h32 { padding-left: 16px !important; padding-right: 16px !important; }

  /* Hero — tighter padding, smaller headline */
  .pw-explore .pw-hero { padding: 24px 16px 22px !important; }
  .pw-explore .pw-hero-title { font-size: 26px !important; line-height: 1.15 !important; }
  .pw-explore .pw-hero-watermark { font-size: 140px !important; right: -40px !important; }

  /* Section heads — smaller h2 */
  .pw-explore .pw-section-head h2 { font-size: 19px !important; }

  /* New launches, news 3-col, picked+hot 2-col → stack */
  .pw-explore .pw-grid-launches  { grid-template-columns: 1fr !important; }
  .pw-explore .pw-grid-hotpicked { grid-template-columns: 1fr !important; gap: 24px !important; }
  .pw-explore .pw-grid-news      { grid-template-columns: 1fr !important; gap: 12px !important; }
  .pw-explore .pw-grid-newsroom  { grid-template-columns: 1fr !important; }
  .pw-explore .pw-grid-launches-all { grid-template-columns: 1fr !important; }

  /* HotRow — drop thumb + price block, keep rank/name/trend */
  .pw-explore .pw-hot-row { grid-template-columns: 26px 1fr auto !important; gap: 10px !important; padding: 10px 12px !important; }
  .pw-explore .pw-hot-row .pw-hot-thumb,
  .pw-explore .pw-hot-row .pw-hot-price { display: none !important; }

  /* Hot chart — collapse 7-col grid to # / name / change */
  .pw-explore .pw-chart-row { grid-template-columns: 26px 1fr 70px !important; gap: 8px !important; padding: 10px 8px !important; }
  .pw-explore .pw-chart-hide { display: none !important; }

  /* BackHeader — tighter padding, allow horizontal scroll if filter chips overflow */
  .pw-explore .pw-bh { padding: 0 16px !important; gap: 10px !important; overflow-x: auto !important; }
  .pw-explore .pw-bh > * { flex-shrink: 0; }

  /* Property detail */
  .pw-explore .pw-prop-wrap { padding: 16px 16px 32px !important; }
  .pw-explore .pw-prop-grid { grid-template-columns: 1fr !important; gap: 20px !important; }
  .pw-explore .pw-prop-stats { grid-template-columns: 1fr 1fr !important; gap: 16px !important; padding: 16px 0 !important; }
  .pw-explore .pw-prop-title { font-size: 28px !important; }
  .pw-explore .pw-prop-hero { height: 220px !important; }
  .pw-explore .pw-prop-aside { position: static !important; padding: 16px !important; }
  .pw-explore .pw-prop-tabs { overflow-x: auto !important; }
  .pw-explore .pw-prop-tabs > button { white-space: nowrap; flex-shrink: 0; }
  .pw-explore .pw-loc-grid { grid-template-columns: 1fr !important; gap: 18px !important; }

  /* Unit-mix table — wrap content for horizontal scroll on small screens */
  .pw-explore .pw-unit-scroll { overflow-x: auto; }
  .pw-explore .pw-unit-scroll > * { min-width: 540px; }

  /* Article */
  .pw-explore .pw-article-wrap  { padding: 24px 16px 48px !important; }
  .pw-explore .pw-article-title { font-size: 28px !important; letter-spacing: -0.03em !important; }
  .pw-explore .pw-article-dek   { font-size: 15px !important; }
  .pw-explore .pw-article-hero  { height: 180px !important; }
  .pw-explore .pw-article-stats { gap: 10px !important; }
  .pw-explore .pw-article-cta   { flex-direction: column !important; align-items: flex-start !important; padding: 18px 18px !important; }
  .pw-explore .pw-article-cta button { width: 100% !important; }
  .pw-explore .pw-article-body  { max-width: 100% !important; }
  .pw-explore .pw-article-body p { font-size: 15.5px !important; }
  .pw-explore .pw-article-body h2 { font-size: 20px !important; }
}
`;

/* ═════════════════════════════════════════════════════════════
   TYPES
   ═════════════════════════════════════════════════════════════ */
export type Tone = "forest" | "cream" | "slate" | "rose" | "sky";

export type LaunchProperty = {
  name: string;
  district: string;
  tone: Tone;
  tag: string;
  blurb?: string;
  priceFrom: string;
  psf: string;
  units: string;
  // Public URL of the project's first gallery asset, when one exists.
  // Absent / empty falls back to the tone-tinted SVG placeholder in PropThumb.
  imageUrl?: string;
};

export type HotProperty = {
  name: string;
  district: string;
  type: string;
  tone: Tone;
  price: string;
  psf: string;
  change: string;
  // PROP-177: raw numeric psf_change_pct for sorting. `change` is the
  // display string (unicode minus / em-dash) and must NOT be parsed back
  // into a number. null = no change data (sinks to the bottom of a PSF sort).
  changePct: number | null;
  dir: "up" | "down" | "flat";
  // txn_count from /explore/hottest (90-day URA window). Replaces the
  // legacy `views` field — there's no real source for view counts.
  txnCount: number;
  imageUrl?: string;
  // PROP-76 — visible-review aggregate. count=0 means "no badge" at
  // the renderer; defaults to 0 so the type stays non-optional and a
  // missing wire field can't sneak undefined into ReviewBadge.
  reviewAvg: number;
  reviewCount: number;
};

export type PickedProperty = {
  name: string;
  district: string;
  tone: Tone;
  matchScore: number;
  // Formatted median price string ("S$2.42M"). Empty when the server
  // returned no median (sparse district / band) — the card hides the
  // price line entirely rather than rendering "S$NaN".
  price: string;
  // Provenance label that always accompanies a non-empty price, e.g.
  // "D15 3BR median (~80–120 sqm), last 90d · n=42". Rendered as a
  // small caption so users never read `price` as a per-project asking
  // figure.
  priceBasis: string;
  why: string;
  imageUrl?: string;
  // PROP-83 — visible-review aggregate; count=0 → ReviewBadge hides.
  reviewAvg: number;
  reviewCount: number;
};

type Article = {
  kind: "URA" | "Policy" | "Market" | "Brief";
  title: string;
  dek?: string;
  heroImage?: string;
  source: string;
  date: string;
  readTime: string;
  // Visible-comment count threaded from BuyerNewsCard (PROP-81). Optional
  // because the static NEWS_FULL fixture and home-feed mock items don't
  // carry one; the renderer hides the badge when it's missing or zero.
  comments?: number;
  // PROP-184: slug + structured body carried from the live article wire
  // so the reader renders the REAL article instead of a hardcoded
  // placeholder. Absent on the offline NEWS_FULL fixture (→ dek fallback).
  slug?: string;
  body?: Section[];
  // PROP-192: author slug carried from the wire so the byline can link to
  // the public /author/{slug} profile. Absent on the offline fixture.
  authorSlug?: string;
};

/* ═════════════════════════════════════════════════════════════
   DATA
   ═════════════════════════════════════════════════════════════ */
const NEW_LAUNCHES: LaunchProperty[] = [
  { name: "The Marlowe Residences", district: "D9 · River Valley", tone: "forest", tag: "New launch", blurb: "Boutique 84-unit freehold along the river, finishes by Yabu Pushelberg.", priceFrom: "S$2.48M", psf: "S$2,890", units: "84" },
  { name: "Lentor Hills Edition",   district: "D26 · Lentor",      tone: "cream",  tag: "Previewing", blurb: "99-yr leasehold next to MRT. Three-tier landscape, sky lounges on 24F.",      priceFrom: "S$1.32M", psf: "S$2,140", units: "598" },
  { name: "Tampines Greenview",     district: "D18 · Tampines",    tone: "sky",    tag: "Balloting",  blurb: "Executive condominium for SC/PR couples earning under S$16k/month.",          priceFrom: "S$1.18M", psf: "S$1,560", units: "696" },
];

const LAUNCHES_FULL: LaunchProperty[] = [
  ...NEW_LAUNCHES,
  { name: "Watten House",               district: "D11 · Bukit Timah",  tone: "forest", tag: "New launch",   blurb: "Freehold redevelopment of Watten Estate. 180 units across 5 blocks.",      priceFrom: "S$3.85M", psf: "S$3,210", units: "180" },
  { name: "The Hill at One-North",      district: "D5 · One-North",     tone: "sky",    tag: "Previewing",   blurb: "Tech-corridor address with co-working sky deck and 50m lap pool.",          priceFrom: "S$1.74M", psf: "S$2,420", units: "142" },
  { name: "Hillock Green",              district: "D26 · Lentor",       tone: "cream",  tag: "Launching",    blurb: "Next door to Lentor MRT. Smart-home wiring standard on every unit.",        priceFrom: "S$1.18M", psf: "S$2,060", units: "474" },
  { name: "Cuscaden Reserve",           district: "D10 · Orchard",      tone: "rose",   tag: "Final phase",  blurb: "Last 24 units of a 99-yr leasehold tower next to Orchard Boulevard MRT.",   priceFrom: "S$3.20M", psf: "S$3,480", units: "24 left" },
  { name: "Hillshore at Pasir Panjang", district: "D5 · Pasir Panjang", tone: "forest", tag: "Balloting",    blurb: "Boutique 59-unit freehold facing Kent Ridge Park.",                         priceFrom: "S$2.10M", psf: "S$2,580", units: "59" },
  { name: "The Botany at Dairy Farm",   district: "D23 · Hillview",     tone: "cream",  tag: "Launching",    blurb: "99-yr leasehold next to Dairy Farm Nature Park. Family-skewed mix.",        priceFrom: "S$1.42M", psf: "S$2,090", units: "385" },
  { name: "Lentoria",                   district: "D26 · Lentor",       tone: "slate",  tag: "New launch",   blurb: "Compact 267-unit project with two-bedroom premium dual-key configurations.", priceFrom: "S$1.05M", psf: "S$2,150", units: "267" },
  { name: "Sora Residences",            district: "D22 · Jurong Lake",  tone: "sky",    tag: "Previewing",   blurb: "Jurong Lake District frontier with direct-link to Garden Bridge.",          priceFrom: "S$1.62M", psf: "S$2,240", units: "440" },
  { name: "Tengah Greenfields EC",      district: "D24 · Tengah",       tone: "forest", tag: "Balloting",    blurb: "Executive condo for SC/PR families. Car-lite estate, walk to MRT.",         priceFrom: "S$1.08M", psf: "S$1,490", units: "548" },
];


const NEWS: Article[] = [
  { kind: "URA",    title: "URA Draft Master Plan 2025: 5 areas that will reshape where you buy", dek: "Greater Southern Waterfront, Jurong Lake District, and three more — what each means for resale values over the next 10 years.", source: "ProProp Editorial", date: "May 9, 2026",  readTime: "6 min" },
  { kind: "Policy", title: "MOP changes are coming — what HDB owners should know before 2027",  source: "MND Bulletin",      date: "May 7, 2026",  readTime: "4 min" },
  { kind: "Market", title: "Q1 2026 private resale: PSF up 1.8% QoQ, volumes stay soft",          source: "ProProp Data Lab", date: "May 5, 2026",  readTime: "5 min" },
  { kind: "Policy", title: "IRAS clarifies ABSD remission for matrimonial-home transfers",       source: "IRAS · official",   date: "Apr 30, 2026", readTime: "3 min" },
  { kind: "Brief",  title: "Weekend brief: 3 cooling-measure rumours, separating signal from noise", source: "ProProp Editorial", date: "May 11, 2026", readTime: "2 min" },
];

const NEWS_FULL: Article[] = [
  ...NEWS,
  { kind: "URA",    title: "Greater Southern Waterfront: timeline pushed 2 years, first plots tendered 2028",        source: "URA · official",     date: "May 2, 2026", readTime: "4 min" },
  { kind: "Market", title: "D9, D10, D11 luxury resale: PSF crosses S$3,000 median for first time",                 source: "ProProp Data Lab",  date: "Apr 28, 2026", readTime: "7 min" },
  { kind: "Policy", title: "CPF Ordinary Account interest rate review: what 2.5% vs 2.7% does to your sums",        source: "CPF Board",          date: "Apr 25, 2026", readTime: "5 min" },
  { kind: "Brief",  title: "En-bloc tracker: 4 collective sales launched in April, only 1 hit reserve",             source: "ProProp Editorial", date: "Apr 22, 2026", readTime: "3 min" },
  { kind: "URA",    title: "Jurong Lake District: new mixed-use plot zoning details released",                       source: "URA · official",     date: "Apr 18, 2026", readTime: "4 min" },
  { kind: "Market", title: "New launch take-up: 5 of 6 launches in March cleared 60% in opening weekend",            source: "ProProp Data Lab",  date: "Apr 15, 2026", readTime: "6 min" },
  { kind: "Policy", title: "TDSR remains at 55% — MAS reaffirms framework, no near-term changes expected",          source: "MAS · statement",    date: "Apr 11, 2026", readTime: "3 min" },
];

/* ═════════════════════════════════════════════════════════════
   PRIMITIVES
   ═════════════════════════════════════════════════════════════ */
function PropThumb({
  tone = "forest",
  tag,
  height = 160,
  imageUrl,
  alt,
}: {
  tone?: Tone;
  tag?: string;
  height?: number;
  imageUrl?: string;
  alt?: string;
}) {
  const palettes: Record<Tone, { bg: string; fg: string; mid: string }> = {
    forest: { bg: "#dfe7e0", fg: "#1d4a3a", mid: "#a8bdac" },
    cream:  { bg: "#f0e7d0", fg: "#6b5320", mid: "#d9c896" },
    slate:  { bg: "#dde2e1", fg: "#3d4a45", mid: "#a3aba6" },
    rose:   { bg: "#ecd9d2", fg: "#7a2d1f", mid: "#cfa495" },
    sky:    { bg: "#d9e3eb", fg: "#2a4a66", mid: "#9eb4c4" },
  };
  const p = palettes[tone];
  // Per-card image-load failure flag; when the upstream URL 404s or
  // the bytes are corrupt, swap to the SVG placeholder rather than
  // showing a broken-image icon.
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = imageUrl && !imgFailed;
  return (
    <div style={{ position: "relative", height, borderRadius: 8, background: p.bg, overflow: "hidden", border: "1px solid var(--rule)" }}>
      {showImage ? (
        <img
          src={imageUrl}
          alt={alt || ""}
          loading="lazy"
          onError={() => setImgFailed(true)}
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <svg viewBox="0 0 300 160" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
          <rect x="40" y="60" width="60" height="100" fill={p.mid} opacity="0.5" />
          <rect x="110" y="30" width="80" height="130" fill={p.fg} opacity="0.85" />
          <rect x="200" y="70" width="70" height="90" fill={p.mid} opacity="0.6" />
          {[0, 1, 2, 3, 4, 5, 6].map((r) =>
            [0, 1, 2].map((c) => (
              <rect key={`${r}-${c}`} x={120 + c * 22} y={42 + r * 16} width="10" height="8" fill={p.bg} opacity="0.8" />
            )),
          )}
          <rect x="0" y="148" width="300" height="12" fill={p.fg} opacity="0.15" />
        </svg>
      )}
      {tag && (
        <div
          style={{
            position: "absolute", top: 10, left: 10,
            fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600,
            letterSpacing: "0.1em", textTransform: "uppercase",
            padding: "3px 7px", borderRadius: 3,
            background: "var(--surface)", color: p.fg,
            border: `1px solid ${p.fg}33`,
          }}
        >
          {tag}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, color: "var(--ink-4)", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.005em" }}>{value}</div>
    </div>
  );
}

function SectionHead({ kicker, title, action, onAction }: { kicker: string; title: string; action?: string; onAction?: () => void }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14, gap: 12 }}>
      <div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 4 }}>{kicker}</div>
        <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500, color: "var(--ink)", margin: 0, letterSpacing: "-0.02em" }}>{title}</h2>
      </div>
      {action && (
        <button onClick={onAction} style={{ background: "transparent", border: "none", fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--accent)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, letterSpacing: "-0.005em", padding: 0 }}>
          {action}
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M3 6h6m-2-2 2 2-2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, count }: { active?: boolean; onClick?: () => void; children: ReactNode; count?: number }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "5px 11px", borderRadius: 20,
        border: "1px solid",
        borderColor: active ? "var(--accent)" : "var(--rule)",
        background: active ? "var(--accent-soft)" : "var(--surface)",
        color: active ? "var(--accent)" : "var(--ink-2)",
        fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: active ? 500 : 400,
        cursor: "pointer", letterSpacing: "-0.005em", transition: "all 150ms",
      }}
    >
      {children}
      {count != null && (
        <span style={{ fontSize: 10, color: active ? "var(--accent)" : "var(--ink-4)", fontFamily: "var(--font-mono)" }}>{count}</span>
      )}
    </button>
  );
}

function BackHeader({ kicker, title, onBack, right, backLabel = "Explore" }: { kicker: string; title: string; onBack: () => void; right?: ReactNode; backLabel?: string }) {
  return (
    <div className="pw-bh" style={{ height: 56, flexShrink: 0, borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", padding: "0 32px", gap: 14, background: "var(--bg)", position: "sticky", top: 0, zIndex: 5 }}>
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--rule)", padding: "5px 10px 5px 8px", borderRadius: 6, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)", cursor: "pointer", letterSpacing: "-0.005em" }}
      >
        <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
          <path d="M9 3l-4 4 4 4M5 7h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {backLabel}
      </button>
      <div style={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)" }}>{kicker}</div>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
      </div>
      <div style={{ flex: 1 }} />
      {right}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   CARDS
   ═════════════════════════════════════════════════════════════ */
function NewLaunchCard({ p, onOpen }: { p: LaunchProperty; onOpen: (name: string) => void }) {
  return (
    <article
      onClick={() => onOpen(p.name)}
      style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, overflow: "hidden", transition: "transform 200ms, border-color 200ms, box-shadow 200ms", cursor: "pointer" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = "var(--rule-strong)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(23,32,29,0.06)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = "var(--rule)"; e.currentTarget.style.boxShadow = "none"; }}
    >
      <PropThumb tone={p.tone} tag={p.tag} height={180} imageUrl={p.imageUrl} alt={p.name} />
      <div style={{ padding: "14px 16px 16px" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-3)", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 4 }}>{p.district}</div>
        <h3 style={{ fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 500, margin: 0, letterSpacing: "-0.015em", color: "var(--ink)" }}>{p.name}</h3>
        {p.blurb && (
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic", color: "var(--ink-2)", margin: "6px 0 12px", lineHeight: 1.5 }}>{p.blurb}</p>
        )}
        <div style={{ display: "flex", gap: 14, paddingTop: 10, borderTop: "1px solid var(--rule)" }}>
          <Stat label="From" value={p.priceFrom} />
          <Stat label="PSF" value={p.psf} />
          <Stat label="Units" value={p.units} />
        </div>
      </div>
    </article>
  );
}

function HotRow({ rank, p, onOpen }: { rank: number; p: HotProperty; onOpen: (name: string) => void }) {
  const dirColor = p.dir === "up" ? "var(--pos)" : p.dir === "down" ? "var(--neg)" : "var(--ink-3)";
  return (
    <button
      onClick={() => onOpen(p.name)}
      className="pw-hot-row"
      style={{ display: "grid", gridTemplateColumns: "28px 64px 1fr auto auto", gap: 14, width: "100%", alignItems: "center", textAlign: "left", padding: "12px 14px", border: "1px solid var(--rule)", borderRadius: 10, background: "var(--surface)", cursor: "pointer", transition: "border-color 150ms, background 150ms" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--rule-strong)"; e.currentTarget.style.background = "var(--surface-2)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--rule)"; e.currentTarget.style.background = "var(--surface)"; }}
    >
      <div style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500, color: "var(--ink-4)", letterSpacing: "-0.02em", fontStyle: "italic" }}>{String(rank).padStart(2, "0")}</div>
      <div className="pw-hot-thumb"><PropThumb tone={p.tone} height={52} imageUrl={p.imageUrl} alt={p.name} /></div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.015em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
          <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", letterSpacing: "-0.005em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{p.district} · {p.type || "—"} · {p.txnCount.toLocaleString("en-SG")} txns · 90d</span>
          {/* PROP-76 — chip hidden when count === 0; surfaces reviews as a discovery signal on the row. */}
          <ReviewBadge avg={p.reviewAvg} count={p.reviewCount} />
        </div>
      </div>
      <div className="pw-hot-price" style={{ textAlign: "right" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 500, color: "var(--ink)" }}>{p.price}</div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)" }}>{p.psf}</div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: "var(--font-mono)", fontSize: 11, fontWeight: 500, color: dirColor, minWidth: 56, justifyContent: "flex-end" }}>
        <svg width="9" height="9" viewBox="0 0 10 10" fill="none" style={{ transform: p.dir === "down" ? "rotate(180deg)" : "rotate(0)" }}>
          <path d="M5 1L1 6h3v3h2V6h3z" fill="currentColor" />
        </svg>
        {p.change}
      </div>
    </button>
  );
}

function PickedCard({ p, onOpen }: { p: PickedProperty; onOpen: (name: string) => void }) {
  // Meta line drops empty segments: a candidate with no district join
  // or no price still renders without dangling "·" separators.
  const metaParts: string[] = [];
  if (p.district) metaParts.push(p.district);
  if (p.price) metaParts.push(p.price);
  const meta = metaParts.join(" · ");
  return (
    <article
      onClick={() => onOpen(p.name)}
      style={{ display: "flex", gap: 14, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", borderRadius: 12, padding: 14, cursor: "pointer", transition: "border-color 150ms, transform 150ms" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ width: 110, flexShrink: 0 }}>
        <PropThumb tone={p.tone} height={110} imageUrl={p.imageUrl} alt={p.name} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <svg width="9" height="9" viewBox="0 0 10 10" fill="none">
            <path d="M5 .5 6.2 3.8 9.5 5 6.2 6.2 5 9.5 3.8 6.2.5 5 3.8 3.8z" fill="currentColor" />
          </svg>
          {p.matchScore}% match
        </div>
        <h3 style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 500, margin: 0, letterSpacing: "-0.015em", color: "var(--ink)" }}>{p.name}</h3>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
          {meta && (
            <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>{meta}</span>
          )}
          {/* PROP-83 — chip hidden when count === 0 (consistent with HotRow). */}
          <ReviewBadge avg={p.reviewAvg} count={p.reviewCount} />
        </div>
        {p.priceBasis && (
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, color: "var(--ink-4)", marginTop: 2, letterSpacing: "-0.005em" }}>{p.priceBasis}</div>
        )}
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ai-accent)", margin: "8px 0 0", lineHeight: 1.45, flex: 1 }}>“{p.why}”</p>
      </div>
    </article>
  );
}

function NewsCard({ a, large, onOpen }: { a: Article; large?: boolean; onOpen: (a: Article) => void }) {
  const palette = {
    URA:    { fg: "#1d4a3a", bg: "#dfe7e0" },
    Policy: { fg: "#6b5320", bg: "#f0e7d0" },
    Market: { fg: "#2a4a66", bg: "#d9e3eb" },
    Brief:  { fg: "#3d4a45", bg: "#dde2e1" },
  }[a.kind];
  return (
    <article
      onClick={() => onOpen(a)}
      style={{ display: "flex", flexDirection: large ? "column" : "row", gap: 14, background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, padding: 14, cursor: "pointer", transition: "border-color 150ms" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--rule-strong)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--rule)"; }}
    >
      <div style={{ width: large ? "100%" : 96, height: large ? 140 : 96, flexShrink: 0, borderRadius: 8, background: palette.bg, position: "relative", overflow: "hidden" }}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
          {a.kind === "URA" && (
            <g stroke={palette.fg} strokeWidth="0.7" fill="none" opacity="0.7">
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => <line key={`v${i}`} x1={i * 10} y1="0" x2={i * 10} y2="100" />)}
              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => <line key={`h${i}`} x1="0" y1={i * 10} x2="100" y2={i * 10} />)}
              <rect x="20" y="30" width="25" height="40" fill={palette.fg} opacity="0.3" />
              <rect x="55" y="20" width="20" height="50" fill={palette.fg} opacity="0.5" />
            </g>
          )}
          {a.kind === "Policy" && (
            <g fill={palette.fg} opacity="0.7">
              <rect x="20" y="20" width="60" height="6" rx="1" />
              <rect x="20" y="34" width="50" height="3" rx="1" opacity="0.5" />
              <rect x="20" y="42" width="55" height="3" rx="1" opacity="0.5" />
              <rect x="20" y="50" width="40" height="3" rx="1" opacity="0.5" />
              <circle cx="78" cy="72" r="14" stroke={palette.fg} strokeWidth="2" fill="none" opacity="0.6" />
              <path d="M73 72l4 4 7-8" stroke={palette.fg} strokeWidth="2" fill="none" opacity="0.6" />
            </g>
          )}
          {a.kind === "Market" && (
            <g fill="none" stroke={palette.fg} strokeWidth="1.4" opacity="0.7">
              <polyline points="10,75 25,60 35,68 50,40 65,50 80,25 92,32" />
              {[[10, 75], [25, 60], [35, 68], [50, 40], [65, 50], [80, 25], [92, 32]].map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="1.6" fill={palette.fg} />
              ))}
              <line x1="5" y1="90" x2="95" y2="90" opacity="0.4" />
            </g>
          )}
          {a.kind === "Brief" && (
            <g fill={palette.fg} opacity="0.6">
              <circle cx="35" cy="50" r="20" opacity="0.4" />
              <circle cx="60" cy="50" r="20" opacity="0.5" />
              <text x="50" y="56" textAnchor="middle" fontSize="14" fontFamily="serif" fontStyle="italic" opacity="0.9">brief</text>
            </g>
          )}
        </svg>
        {a.heroImage && (
          <img
            src={a.heroImage}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
        <div style={{ position: "absolute", top: 8, left: 8, fontFamily: "var(--font-ui)", fontSize: 9, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 7px", borderRadius: 3, background: "var(--surface)", color: palette.fg }}>{a.kind}</div>
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <h3 style={{ fontFamily: "var(--font-serif)", fontSize: large ? 17 : 14, fontWeight: 500, margin: 0, color: "var(--ink)", letterSpacing: "-0.015em", lineHeight: 1.25 }}>{a.title}</h3>
        {large && a.dek && (
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-2)", margin: "8px 0 0", lineHeight: 1.5 }}>{a.dek}</p>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)", marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span>{a.source}</span>
          <span style={{ width: 2, height: 2, borderRadius: 2, background: "var(--ink-4)" }} />
          <span>{a.date}</span>
          <span style={{ width: 2, height: 2, borderRadius: 2, background: "var(--ink-4)" }} />
          <span>{a.readTime}</span>
          {/* PROP-81: comment-count badge surfaces discussion volume on
              the card. Hidden at zero so the meta row stays calm for
              brand-new articles; aria-label so screen readers don't
              read the emoji as "speech balloon comma N". */}
          {a.comments != null && a.comments > 0 && (
            <>
              <span style={{ width: 2, height: 2, borderRadius: 2, background: "var(--ink-4)" }} />
              <span aria-label={`${a.comments} comment${a.comments === 1 ? "" : "s"}`}>
                <span aria-hidden="true">💬</span> {a.comments}
              </span>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

/* ═════════════════════════════════════════════════════════════
   HERO
   ═════════════════════════════════════════════════════════════ */

// useGreeting hydrates the Explore hero with the live /explore/greeting
// response (PROP-71). Returns a state that always renders something:
// while the fetch is in flight (or if it fails), we fall back to a
// client-computed time-of-day and the static headline so the hero
// shape is stable across the initial paint → response swap.
//
// CLAUDE.md "optimistic UI must equal post-refresh UI": the fallback
// has the same `{timeOfDay, headline}` shape as the server response,
// so the swap is a pure text update — no layout shift, no flicker
// from a hidden subtree mounting.
function useGreeting(): GreetingResp {
  const [g, setG] = useState<GreetingResp>(() => ({
    timeOfDay: bucketTimeOfDayClientFallback(),
    headline: GREETING_FALLBACK_HEADLINE,
    kind: "fallback",
  }));
  useEffect(() => {
    let cancelled = false;
    getGreeting()
      .then((resp) => {
        if (cancelled) return;
        setG(resp);
      })
      .catch(() => {
        // Network blip → keep the fallback. Logging a console.warn
        // here would be noisy in offline dev; the static line is the
        // graceful degradation.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return g;
}

// timeOfDayLabel renders the leading "Good morning/afternoon/evening"
// half of the hero. Kept separate so the bucket value drives both
// halves consistently (FE label + server-side bucket use the same
// strings).
function timeOfDayLabel(t: GreetingResp["timeOfDay"]): string {
  switch (t) {
    case "morning":
      return "Good morning";
    case "afternoon":
      return "Good afternoon";
    case "evening":
      return "Good evening";
    default:
      return "Hello";
  }
}

function ExploreHero({ onAsk }: { onAsk: (q: string) => void }) {
  const today = new Date().toLocaleDateString("en-SG", { weekday: "long", day: "numeric", month: "short" });
  const greeting = useGreeting();
  return (
    <section className="pw-hero" style={{ position: "relative", overflow: "hidden", padding: "32px 32px 28px", borderBottom: "1px solid var(--rule)", background: "linear-gradient(180deg, var(--surface) 0%, var(--bg) 100%)" }}>
      <div aria-hidden className="pw-hero-watermark" style={{ position: "absolute", right: -30, top: -10, fontFamily: "var(--font-serif)", fontSize: 220, fontStyle: "italic", fontWeight: 400, color: "var(--ink)", opacity: 0.04, lineHeight: 1, letterSpacing: "-0.04em", pointerEvents: "none", userSelect: "none" }}>
        Explore
      </div>
      <div style={{ position: "relative", maxWidth: 1100 }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 6, height: 6, borderRadius: 6, background: "var(--pos)" }} />
          {today}
        </div>
        <h1 className="pw-hero-title" style={{ fontFamily: "var(--font-serif)", fontSize: 36, fontWeight: 500, margin: 0, letterSpacing: "-0.03em", color: "var(--ink)", lineHeight: 1.1, maxWidth: 720 }}>
          {/* PROP-71: time-of-day + headline driven by /explore/greeting.
              The fallback initial state has the same shape as the server
              response, so the swap is a single text update — no shift. */}
          {timeOfDayLabel(greeting.timeOfDay)}. <span style={{ fontStyle: "italic", color: "var(--ink-2)" }}>{greeting.headline}</span>
        </h1>
        <div style={{ marginTop: 22, display: "flex", flexWrap: "wrap", gap: 8 }}>
          {[
            "Pick up Bishan vs AMK search",
            "What changed since I last checked",
            "Run my affordability with new TDSR",
            "Hottest 3BR under S$2.5M",
          ].map((q) => (
            <button
              key={q}
              onClick={() => onAsk(q)}
              style={{ background: "var(--surface)", border: "1px solid var(--rule)", padding: "7px 13px", borderRadius: 20, fontFamily: "var(--font-serif)", fontSize: 12.5, fontStyle: "italic", color: "var(--ink-2)", cursor: "pointer", transition: "all 150ms" }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.background = "var(--accent-soft)"; e.currentTarget.style.color = "var(--accent)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--rule)"; e.currentTarget.style.background = "var(--surface)"; e.currentTarget.style.color = "var(--ink-2)"; }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════════════════════════════════════════════
   SUB-VIEW: LAUNCHES ALL
   ═════════════════════════════════════════════════════════════ */
// useHottest returns the Hottest-projects feed aggregated from URA
// transactions over a 90-day window. Empty until the network call lands
// (or quietly empty when VITE_EXPLORE_API is unset). There's no
// meaningful seed fixture for live transaction stats, so failure
// degrades to an empty list — the section header still renders.
function useHottest(limit = 15): HotProperty[] {
  const [items, setItems] = useState<HotProperty[]>([]);
  useEffect(() => {
    if (!exploreApiEnabled()) return;
    let cancelled = false;
    listHottest(limit)
      .then((rows) => {
        if (!cancelled) setItems(rows.map(adaptHottestRow));
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);
  return items;
}

// usePicked returns the buyer "Picked for you" feed, scored server-side
// against the signed-in user's workspace prefs. Returns both the rows
// and a `reason` string so the caller can branch on the empty-state
// hints emitted by /explore/picked ("no_workspace" / "no_prefs" /
// "no_candidates"). Re-runs when the workspace identity changes — picks
// depend on the workspace's prefs, so a freshly-saved workspace edit
// should repopulate the section.
function usePicked(workspaceId: string | undefined): { items: PickedProperty[]; reason: string; loading: boolean } {
  const [items, setItems] = useState<PickedProperty[]>([]);
  const [reason, setReason] = useState<string>("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!exploreApiEnabled()) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listPicked(workspaceId)
      .then((resp) => {
        if (cancelled) return;
        setItems((resp.rows ?? []).map(adaptPickedRow));
        setReason(resp.reason ?? "");
      })
      .catch(() => {
        if (cancelled) return;
        // 401 (anonymous) and network failures collapse to an empty
        // list — the empty-state below renders a generic "sign in to
        // see picks" hint that fits both cases.
        setItems([]);
        setReason("no_workspace");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [workspaceId]);
  return { items, reason, loading };
}

// useLaunches returns the New-launches feed from the URA pipeline,
// falling back to the hardcoded LAUNCHES_FULL fixture when
// VITE_EXPLORE_API is unset or the call fails. Shape matches the local
// LaunchProperty type so the cards render unchanged.
function useLaunches(): LaunchProperty[] {
  const [items, setItems] = useState<LaunchProperty[]>(() =>
    exploreApiEnabled() ? [] : LAUNCHES_FULL,
  );
  useEffect(() => {
    if (!exploreApiEnabled()) return;
    let cancelled = false;
    listPipeline()
      .then((rows) => {
        if (!cancelled) setItems(rows.map(adaptPipelineRow));
      })
      .catch(() => {
        // Network failure → quietly fall back to the seed so the section
        // still renders.
        if (!cancelled) setItems(LAUNCHES_FULL);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return items;
}

function LaunchesAllView({ onBack, onOpenProperty, launches }: { onBack: () => void; onOpenProperty: (name: string) => void; launches: LaunchProperty[] }) {
  const [filter, setFilter] = useState<string>("All");
  const tags = ["All", "New launch", "Previewing", "Launching", "Balloting", "Final phase"];
  const counts: Record<string, number> = { All: launches.length };
  launches.forEach((p) => { counts[p.tag] = (counts[p.tag] || 0) + 1; });
  const filtered = filter === "All" ? launches : launches.filter((p) => p.tag === filter);

  return (
    <div className="pw-explore" style={mainStyle()}>
      <BackHeader
        kicker="Just listed"
        title={`${launches.length} new launches`}
        onBack={onBack}
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
            Sort:
            <select style={{ background: "var(--surface)", border: "1px solid var(--rule)", padding: "4px 8px", borderRadius: 6, fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)", cursor: "pointer" }}>
              <option>Most recent</option>
              <option>Price: low → high</option>
              <option>Price: high → low</option>
              <option>PSF: low → high</option>
              <option>Units (largest)</option>
            </select>
          </div>
        }
      />
      <section className="pw-h32" style={{ padding: "20px 32px 8px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {tags.map((t) => (
            <FilterChip key={t} active={filter === t} onClick={() => setFilter(t)} count={counts[t]}>{t}</FilterChip>
          ))}
        </div>
      </section>
      <section className="pw-h32" style={{ padding: "20px 32px 48px", maxWidth: 1200, margin: "0 auto" }}>
        <div className="pw-grid-launches-all" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {filtered.map((p, i) => <NewLaunchCard key={i} p={p} onOpen={onOpenProperty} />)}
        </div>
        {filtered.length === 0 && (
          <div style={{ padding: "40px", textAlign: "center", color: "var(--ink-3)", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 14 }}>No launches in this state.</div>
        )}
      </section>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   SUB-VIEW: HOT CHART
   ═════════════════════════════════════════════════════════════ */

function HotChartView({ onBack, onOpenProperty, hottest }: { onBack: () => void; onOpenProperty: (name: string) => void; hottest: HotProperty[] }) {
  const [sort, setSort] = useState<"txns" | "change-up" | "change-down">("txns");
  // PROP-177: sort on the raw numeric changePct (sortHotChart), never the
  // unicode-minus display string. The rank shown below is the post-sort
  // render index, so it always agrees with the row order + top-3 accent.
  const sorted = sortHotChart(hottest, sort);

  return (
    <div className="pw-explore" style={mainStyle()}>
      <BackHeader
        kicker="Trending"
        title={`The hot chart — top ${hottest.length || 15}`}
        onBack={onBack}
        right={
          <div style={{ display: "flex", gap: 6 }}>
            <FilterChip active={sort === "txns"}        onClick={() => setSort("txns")}>By transactions</FilterChip>
            <FilterChip active={sort === "change-up"}   onClick={() => setSort("change-up")}>By PSF ↑</FilterChip>
            <FilterChip active={sort === "change-down"} onClick={() => setSort("change-down")}>By PSF ↓</FilterChip>
          </div>
        }
      />
      <section className="pw-h32" style={{ padding: "24px 32px 8px", maxWidth: 1100, margin: "0 auto" }}>
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontStyle: "italic", color: "var(--ink-2)", margin: 0, maxWidth: 640 }}>
          Projects with the most URA-recorded transactions over the last 90 days. PSF change compares the trailing 45-day median against the 45 days before that.
        </p>
      </section>
      <section className="pw-h32" style={{ padding: "20px 32px 48px", maxWidth: 1100, margin: "0 auto" }}>
        <div className="pw-chart-row" style={{ display: "grid", gridTemplateColumns: "32px 64px 1fr 100px 90px 60px 80px", gap: 14, padding: "0 14px 8px", fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-4)", borderBottom: "1px solid var(--rule)" }}>
          <div>#</div>
          <div className="pw-chart-hide"></div>
          <div>Project</div>
          <div className="pw-chart-hide" style={{ textAlign: "right" }}>Median price</div>
          <div className="pw-chart-hide" style={{ textAlign: "right" }}>PSF</div>
          <div style={{ textAlign: "right" }}>PSF Δ</div>
          <div className="pw-chart-hide" style={{ textAlign: "right" }}>90d txns</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 4 }}>
          {sorted.length === 0 && (
            <div style={{ padding: "40px", textAlign: "center", color: "var(--ink-3)", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 14 }}>
              No transactions in the last 90 days yet.
            </div>
          )}
          {sorted.map((p, i) => {
            const dirColor = p.dir === "up" ? "var(--pos)" : p.dir === "down" ? "var(--neg)" : "var(--ink-3)";
            return (
              <div
                key={i}
                onClick={() => onOpenProperty(p.name)}
                className="pw-chart-row"
                style={{ display: "grid", gridTemplateColumns: "32px 64px 1fr 100px 90px 60px 80px", gap: 14, alignItems: "center", padding: "10px 14px", borderRadius: 8, cursor: "pointer", transition: "background 150ms" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ fontFamily: "var(--font-serif)", fontSize: 18, fontWeight: 500, color: i < 3 ? "var(--accent)" : "var(--ink-4)", fontStyle: "italic", letterSpacing: "-0.02em" }}>{String(i + 1).padStart(2, "0")}</div>
                <div className="pw-chart-hide"><PropThumb tone={p.tone} height={44} imageUrl={p.imageUrl} alt={p.name} /></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.015em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>{p.district}{p.type ? ` · ${p.type}` : ""}</div>
                </div>
                <div className="pw-chart-hide" style={{ textAlign: "right", fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 500, color: "var(--ink)" }}>{p.price}</div>
                <div className="pw-chart-hide" style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-2)" }}>{p.psf}</div>
                <div style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 11, color: dirColor, fontWeight: 500 }}>{p.change}</div>
                <div className="pw-chart-hide" style={{ textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-2)" }}>{p.txnCount.toLocaleString("en-SG")}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   SUB-VIEW: NEWSROOM
   ═════════════════════════════════════════════════════════════ */
// useNewsFeed returns the buyer Newsroom feed, falling back to the
// hardcoded NEWS_FULL fixture when VITE_ARTICLES_API is unset. The
// returned shape matches the local `Article` type so the renderers
// don't need to change.
// adaptNewsArticle maps a live ArticleWire to the buyer Article shape,
// CARRYING the slug + structured body so ArticleView renders the real
// article instead of the hardcoded placeholder (PROP-184). Card-level
// fields reuse adaptBuyerCard; body comes straight off the wire.
export function adaptNewsArticle(w: ArticleWire): Article {
  const c = adaptBuyerCard(w);
  return {
    kind: c.kind,
    title: c.title,
    dek: c.dek,
    heroImage: c.heroImage,
    source: c.source,
    date: c.date,
    readTime: c.readTime,
    comments: c.comments,
    slug: c.slug,
    body: w.body,
    authorSlug: c.authorSlug,
  };
}

function useNewsFeed(): Article[] {
  const [items, setItems] = useState<Article[]>(() =>
    articlesApiEnabled() ? [] : NEWS_FULL,
  );
  useEffect(() => {
    if (!articlesApiEnabled()) return;
    let cancelled = false;
    listNews("", 24)
      .then((resp) => {
        if (cancelled) return;
        const wire = resp.lead ? [resp.lead, ...resp.briefs] : resp.briefs;
        // Wire kinds include "Guide"; adaptNewsArticle narrows / coerces
        // to the public buyer Article union and carries slug + body.
        const cards = wire.map(adaptNewsArticle);
        setItems(cards);
      })
      .catch(() => {
        // Network failure → quietly fall back to seed so the Newsroom
        // page still renders. Surfacing a banner is a follow-up.
        if (!cancelled) setItems(NEWS_FULL);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return items;
}

function NewsroomView({ onBack, onOpenArticle }: { onBack: () => void; onOpenArticle: (a: Article) => void }) {
  const [filter, setFilter] = useState<string>("All");
  const feed = useNewsFeed();
  const filtered = filter === "All" ? feed : feed.filter((n) => n.kind === filter);
  const counts: Record<string, number> = { All: feed.length };
  feed.forEach((n) => { counts[n.kind] = (counts[n.kind] || 0) + 1; });
  const lead = filtered[0];
  const rest = filtered.slice(1);

  return (
    <div className="pw-explore" style={mainStyle()}>
      <BackHeader kicker="Newsroom" title="URA, policy & market" onBack={onBack} />
      <section className="pw-h32" style={{ padding: "28px 32px 8px", maxWidth: 1200, margin: "0 auto" }}>
        <h1 className="pw-hero-title" style={{ fontFamily: "var(--font-serif)", fontSize: 30, fontWeight: 500, margin: 0, letterSpacing: "-0.03em", color: "var(--ink)", maxWidth: 720, lineHeight: 1.1 }}>
          Dispatches from the desk — <span style={{ fontStyle: "italic", color: "var(--ink-2)" }}>plans, policy, and prices, weekly.</span>
        </h1>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 18 }}>
          {["All", "URA", "Policy", "Market", "Brief"].map((k) => (
            <FilterChip key={k} active={filter === k} onClick={() => setFilter(k)} count={counts[k]}>{k}</FilterChip>
          ))}
        </div>
      </section>
      <section className="pw-h32" style={{ padding: "24px 32px 48px", maxWidth: 1200, margin: "0 auto" }}>
        {lead && <div style={{ marginBottom: 24 }}><NewsCard a={lead} large onOpen={onOpenArticle} /></div>}
        <div className="pw-grid-newsroom" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 14 }}>
          {rest.map((a, i) => <NewsCard key={i} a={a} onOpen={onOpenArticle} />)}
        </div>
      </section>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════
   SUB-VIEW: PROPERTY DETAIL
   ═════════════════════════════════════════════════════════════ */
function PropertyHero({ tone = "forest", imageUrl, alt }: { tone?: Tone; imageUrl?: string; alt?: string }) {
  const palettes: Record<Tone, { bg: string; fg: string; mid: string; sky: string }> = {
    forest: { bg: "#dfe7e0", fg: "#1d4a3a", mid: "#a8bdac", sky: "#cad6cc" },
    cream:  { bg: "#f0e7d0", fg: "#6b5320", mid: "#d9c896", sky: "#e9ddb8" },
    slate:  { bg: "#dde2e1", fg: "#3d4a45", mid: "#a3aba6", sky: "#cdd3d2" },
    rose:   { bg: "#ecd9d2", fg: "#7a2d1f", mid: "#cfa495", sky: "#e0cac1" },
    sky:    { bg: "#d9e3eb", fg: "#2a4a66", mid: "#9eb4c4", sky: "#c9d6df" },
  };
  const p = palettes[tone];
  return (
    <div className="pw-prop-hero" style={{ position: "relative", height: 360, borderRadius: 14, overflow: "hidden", background: `linear-gradient(180deg, ${p.sky} 0%, ${p.bg} 100%)`, border: "1px solid var(--rule)" }}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={alt || ""}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <svg viewBox="0 0 800 360" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
          <circle cx="640" cy="80" r="36" fill={p.bg} opacity="0.5" />
          <rect x="40"  y="170" width="80"  height="190" fill={p.mid} opacity="0.4" />
          <rect x="125" y="200" width="60"  height="160" fill={p.mid} opacity="0.5" />
          <rect x="190" y="150" width="70"  height="210" fill={p.mid} opacity="0.55" />
          <rect x="540" y="180" width="100" height="180" fill={p.mid} opacity="0.45" />
          <rect x="645" y="210" width="70"  height="150" fill={p.mid} opacity="0.4" />
          <rect x="280" y="80"  width="230" height="280" fill={p.fg} opacity="0.9" />
          {[0,1,2,3,4,5,6,7,8,9,10,11].map((r) =>
            [0,1,2,3,4,5].map((c) => (
              <rect key={`${r}-${c}`} x={290 + c * 36} y={94 + r * 22} width="22" height="12" fill={p.bg} opacity={r % 3 === 0 ? 0.85 : 0.55} />
            )),
          )}
          <circle cx="50"  cy="340" r="22" fill={p.fg} opacity="0.5" />
          <circle cx="78"  cy="335" r="18" fill={p.fg} opacity="0.45" />
          <circle cx="730" cy="340" r="26" fill={p.fg} opacity="0.5" />
          <circle cx="700" cy="345" r="18" fill={p.fg} opacity="0.4" />
          <rect x="0" y="346" width="800" height="14" fill={p.fg} opacity="0.2" />
        </svg>
      )}
    </div>
  );
}

function PStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, color: "var(--ink-4)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function OverviewTab({ detail }: { detail: PropertyDetail }) {
  // Prose comes straight from project_details.description; split on
  // blank lines so multi-paragraph copy keeps its breaks.
  const paras = (detail.description || "")
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  // "What's included" is built defensively from the volatile
  // specs_parsed blob — only flat string/number fields are shown;
  // structured keys (unit mix, price range, top year) are handled
  // elsewhere or deliberately not surfaced.
  const SKIP = new Set(["unit_types", "price_range", "top_year", "tenure", "district"]);
  const specRows = Object.entries(detail.specs)
    .filter(
      ([k, v]) =>
        !SKIP.has(k) &&
        (typeof v === "string" || typeof v === "number") &&
        String(v).trim() !== "",
    )
    .map(([k, v]) => ({
      label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      value: String(v),
    }));
  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "0 0 12px", letterSpacing: "-0.02em" }}>The project</h2>
      {paras.length > 0 ? (
        paras.map((para, i) => (
          <p key={i} style={{ fontFamily: "var(--font-serif)", fontSize: 15, lineHeight: 1.65, color: "var(--ink-2)", margin: "0 0 14px" }}>{para}</p>
        ))
      ) : (
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 14.5, fontStyle: "italic", color: "var(--ink-3)", margin: "0 0 14px" }}>No project description is available yet.</p>
      )}
      {specRows.length > 0 && (
        <>
          <h3 style={{ fontFamily: "var(--font-serif)", fontSize: 17, fontWeight: 500, color: "var(--ink)", margin: "18px 0 12px", letterSpacing: "-0.015em" }}>What's included</h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "8px 24px" }}>
            {specRows.map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink-2)", lineHeight: 1.5 }}>
                <span style={{ fontWeight: 600, color: "var(--ink-3)" }}>{s.label}:</span>
                {s.value}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AmenityRow({ icon, name, walk, type }: { icon: string; name: string; walk: string; type: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px dashed var(--rule)" }}>
      <div style={{ width: 28, height: 28, borderRadius: 7, flexShrink: 0, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-ui)", fontWeight: 600, fontSize: 12 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 13.5, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>{name}</div>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>{type}</div>
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 11.5, color: "var(--ink-2)" }}>{walk}</div>
    </div>
  );
}

function LocationTab({ tone, detail }: { tone: Tone; detail: PropertyDetail }) {
  const palettes: Record<Tone, { fg: string; bg: string; mid: string }> = {
    forest: { fg: "#1d4a3a", bg: "#dfe7e0", mid: "#a8bdac" },
    cream:  { fg: "#6b5320", bg: "#f0e7d0", mid: "#d9c896" },
    slate:  { fg: "#3d4a45", bg: "#dde2e1", mid: "#a3aba6" },
    rose:   { fg: "#7a2d1f", bg: "#ecd9d2", mid: "#cfa495" },
    sky:    { fg: "#2a4a66", bg: "#d9e3eb", mid: "#9eb4c4" },
  };
  const p = palettes[tone];
  const mrt = detail.nearbyMrt;
  // URA station names arrive UPPERCASE and suffixed ("PASIR RIS MRT
  // STATION"). Titlecase, keep the "MRT" acronym, drop the trailing
  // " Station" so the row reads "Pasir Ris MRT".
  const cleanStation = (raw: string) =>
    raw
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .replace(/\bMrt\b/g, "MRT")
      .replace(/ Station$/i, "");
  // OneMap's official embeddable mini-map. Loaded as a sandboxed
  // iframe — no JS lib needed, SG-native cartography, the marker
  // centres on the project's URA-canonical lat/lng. Falls back to the
  // SVG placeholder when coords are missing (pipeline rows often are).
  const hasCoords = typeof detail.lat === "number" && typeof detail.lng === "number";
  const oneMapSrc = hasCoords
    ? `https://www.onemap.gov.sg/amm/amm.html?mapStyle=Default&zoomLevel=17&marker=latLng:${detail.lat},${detail.lng}!colour:red&popupWidth=200`
    : "";
  return (
    <div className="pw-loc-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28 }}>
      <div>
        <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "0 0 14px", letterSpacing: "-0.02em" }}>The neighbourhood</h2>
        <div style={{ aspectRatio: "1 / 1", borderRadius: 12, overflow: "hidden", background: p.bg, border: "1px solid var(--rule)", position: "relative" }}>
          {hasCoords ? (
            <iframe
              title={`Map of ${detail.name}`}
              src={oneMapSrc}
              loading="lazy"
              referrerPolicy="no-referrer"
              sandbox="allow-scripts allow-same-origin allow-popups"
              style={{ width: "100%", height: "100%", border: 0, display: "block" }}
            />
          ) : (
            <svg viewBox="0 0 200 200" style={{ width: "100%", height: "100%" }}>
              <g stroke={p.mid} strokeWidth="3" opacity="0.6">
                <line x1="0"   y1="60"  x2="200" y2="80" />
                <line x1="0"   y1="130" x2="200" y2="120" />
                <line x1="50"  y1="0"   x2="65"  y2="200" />
                <line x1="140" y1="0"   x2="135" y2="200" />
              </g>
              <path d="M0 175 Q60 165 100 178 T200 170" stroke={p.fg} strokeWidth="6" fill="none" opacity="0.4" />
              <circle cx="35"  cy="40" r="22" fill={p.fg} opacity="0.18" />
              <circle cx="170" cy="50" r="18" fill={p.fg} opacity="0.18" />
              <circle cx="100" cy="100" r="14" fill={p.fg} />
              <circle cx="100" cy="100" r="22" fill="none" stroke={p.fg} strokeWidth="1.5" opacity="0.4" />
              <circle cx="100" cy="100" r="32" fill="none" stroke={p.fg} strokeWidth="1" opacity="0.2" />
              <rect x="60" y="63" width="10" height="10" fill="var(--surface)" stroke={p.fg} strokeWidth="1.5" />
              <text x="65" y="71" textAnchor="middle" fontSize="6" fontFamily="var(--font-ui)" fill={p.fg}>M</text>
              <rect x="130" y="115" width="12" height="10" fill="var(--surface)" stroke={p.fg} strokeWidth="1.5" />
              <text x="136" y="123" textAnchor="middle" fontSize="6" fontFamily="var(--font-ui)" fill={p.fg}>S</text>
            </svg>
          )}
          {detail.planningArea && (
            <div style={{ position: "absolute", top: 12, left: 12, fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: p.fg, background: "var(--surface)", padding: "3px 7px", borderRadius: 3, pointerEvents: "none" }}>{detail.planningArea}</div>
          )}
        </div>
        {hasCoords && (
          <div style={{ marginTop: 6, fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-4)" }}>
            Map data © <a href="https://www.onemap.gov.sg/" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>OneMap</a>, Singapore Land Authority
          </div>
        )}
      </div>
      <div>
        <h3 style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", margin: "0 0 4px", letterSpacing: "-0.01em" }}>Nearby MRT</h3>
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-3)", margin: "0 0 10px" }}>Walking time from the project.</p>
        {mrt.length > 0 ? (
          mrt.map((s, i) => (
            <AmenityRow
              key={i}
              icon="M"
              name={cleanStation(s.station)}
              type={s.lines.length > 0 ? s.lines.join(" · ") : s.codes.join(" · ")}
              walk={`${s.walk_min} min`}
            />
          ))
        ) : (
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-3)", margin: 0 }}>No MRT proximity data for this project yet.</p>
        )}
      </div>
    </div>
  );
}

// "YYYY-MM" → "May 2026". Returns the raw string if it doesn't parse, so
// a malformed period is never dropped silently.
function monthLabel(period?: string): string | undefined {
  if (!period) return undefined;
  const m = /^(\d{4})-(\d{2})$/.exec(period);
  if (!m) return period;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mi = parseInt(m[2], 10) - 1;
  return mi >= 0 && mi < 12 ? `${months[mi]} ${m[1]}` : period;
}

// Human-readable provenance for the resolved sample price. The basis
// ALWAYS travels with the number so a district median or the generic
// default can never read as this project's asking price.
function samplePriceBasis(s: SamplePriceResult): { short: string; long: string } {
  const when = monthLabel(s.period);
  switch (s.source) {
    case "dev_sales":
      return {
        short: "Developer-sales median",
        long: `the developer's reported median transacted price${when ? ` (${when})` : ""}`,
      };
    case "txn_median": {
      const n = s.sampleCount ?? 0;
      const sales = n > 0 ? `${n} recent sale${n === 1 ? "" : "s"}` : "recent sales";
      return {
        short: "Median transacted price",
        long: `the median of ${sales}${when ? ` to ${when}` : ""}`,
      };
    }
    case "district_median":
      return {
        short: "District median",
        long: `the district's recent median transacted price${when ? ` (${when})` : ""} — no sales on record for this project yet`,
      };
    case "default":
    default:
      return {
        short: "Generic sample figure",
        long: "a generic worked example — no price data on record for this project yet",
      };
  }
}

// CostsTab — the worked stamp-duty / financing example. The purchase
// quantum is resolved from LIVE data (resolveSamplePrice: dev-sales
// median → recent transacted median → district median → labelled
// default) in code, never from the LLM. Stamp-duty / LTV / stress-rate
// remain fixed illustrative assumptions; only the quantum is sourced.
function CostsTab({ projectName, district }: { projectName: string; district?: string }) {
  const [sample, setSample] = useState<SamplePriceResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSample(null);
    // resolveSamplePrice never rejects — it degrades to the labelled
    // default — but guard anyway so an unexpected throw still lands on
    // the default rather than leaving the tab stuck loading.
    resolveSamplePrice(projectName, district)
      .then((r) => {
        if (!cancelled) setSample(r);
      })
      .catch(() => {
        if (!cancelled) setSample({ price: COSTS_DEFAULT_SAMPLE_PRICE, source: "default" });
      });
    return () => {
      cancelled = true;
    };
  }, [projectName, district]);

  const fmt = (n: number) => "S$" + Math.round(n).toLocaleString("en-SG");

  // Deliberate loading state — resolving the quantum server-side is a
  // read, so we show a placeholder rather than flashing the default
  // figure and then mutating it once the live price lands.
  if (!sample) {
    return (
      <div style={{ maxWidth: 600 }}>
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontStyle: "italic", color: "var(--ink-3)" }}>Working out an illustrative cost breakdown…</p>
      </div>
    );
  }

  const num = sample.price;
  const basis = samplePriceBasis(sample);
  const priceM = `S$${(num / 1_000_000).toFixed(2)}M`;
  // BSD from the canonical post-2023 residential schedule (constants/calc,
  // mirroring the Go constants.BSDResidentialBands) — ~S$93,600 on S$2.48M, scaling with the
  // live quantum. ABSD is intentionally omitted: it depends on the buyer's
  // residency and how many properties they already hold, which an
  // illustrative sample can't assume; iOS (PROP-244) omits it identically
  // so the two surfaces agree.
  const bsd = estBsd(num);
  const downPmt = num * COSTS_DOWN_PAYMENT_RATE;
  const loanAmt = num * COSTS_LTV_RATE;
  const r = COSTS_STRESS_RATE / 12;
  const n = COSTS_LOAN_TERM_YEARS * 12;
  const monthly = (loanAmt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);

  const Row = ({ label, sub, value, total }: { label: string; sub?: string; value: string; total?: boolean }) => (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", padding: "10px 0", borderBottom: total ? "none" : "1px solid var(--rule)", borderTop: total ? "2px solid var(--ink)" : "none", marginTop: total ? 8 : 0 }}>
      <div>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: total ? 500 : 400, color: "var(--ink)", letterSpacing: "-0.01em" }}>{label}</div>
        {sub && <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{sub}</div>}
      </div>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: total ? 16 : 13, fontWeight: total ? 600 : 500, color: total ? "var(--accent)" : "var(--ink)" }}>{value}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 600 }}>
      <div style={{ display: "inline-block", fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--warn)", background: "var(--warn-soft, rgba(180,120,20,0.12))", border: "1px solid var(--warn)", padding: "3px 8px", borderRadius: 4, marginBottom: 10 }}>Illustrative example</div>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "0 0 6px", letterSpacing: "-0.02em" }}>Total cost breakdown</h2>
      <p style={{ fontFamily: "var(--font-serif)", fontSize: 13.5, fontStyle: "italic", color: "var(--ink-3)", margin: "0 0 18px" }}>A worked example for a first residential purchase at {priceM} (75% LTV, 25-yr loan at 4% stressed rate). The purchase price is drawn from {basis.long}. Additional Buyer's Stamp Duty (ABSD) depends on your residency and the properties you already hold, and is excluded here — illustrative, not a quote.</p>
      <div style={{ background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 12, padding: "6px 20px 20px" }}>
        <Row label="Purchase price" sub={basis.short} value={fmt(num)} />
        <Row label="Buyer's Stamp Duty (BSD)" sub="IRAS · progressive schedule (from 15 Feb 2023)" value={fmt(bsd)} />
        <Row label="Down payment (25%)" sub="5% cash, 20% cash/CPF" value={fmt(downPmt)} />
        <Row label="Loan amount (75%)" sub="HDB cap at 4.0% TDSR" value={fmt(loanAmt)} />
        <Row label="Monthly repayment" sub="25-yr loan at 4.0% stressed" value={fmt(monthly) + "/mo"} total />
      </div>
      <p style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-3)", marginTop: 12, lineHeight: 1.5 }}>For illustration only. Run a full affordability check with your actual TDSR ratio and CPF balance for accurate numbers.</p>
    </div>
  );
}

// "unknown" (case-insensitive) is a sentinel emitted by the floorplan
// extractor when a field could not be classified. Filter it out at the
// display boundary so a chip never reads "Unknown".
function isUnknown(v: string | null | undefined): boolean {
  return !!v && v.trim().toLowerCase() === "unknown";
}

// UnitPlansTab — renders one card per plan_type from
// /dataengine/projects/{name}/floorplan, joined to floor_plan assets by
// source_image_sha256s so each card surfaces its actual floor plan
// image. Fetches lazily on mount; either endpoint absent ⇒ empty state.
function UnitPlansTab({ projectName }: { projectName: string }) {
  const [plans, setPlans] = useState<PlanTypeWire[] | null>(null);
  const [assets, setAssets] = useState<AssetRowWire[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "empty" | "error">("loading");
  // Multi-select bedrooms filter. Empty = no narrowing.
  const [selectedBedrooms, setSelectedBedrooms] = useState<number[]>([]);
  // Lightbox carousel state — opens over a single plan_type's images
  // (or the "other" bucket when planIdx is -1). Esc closes; arrow keys
  // navigate within that plan's images.
  const [lightbox, setLightbox] = useState<{ planIdx: number; imgIdx: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatus("loading");
    setPlans(null);
    setAssets([]);
    setSelectedBedrooms([]);
    setLightbox(null);
    Promise.all([
      getProjectFloorplan(projectName).catch(() => null),
      listProjectAssets(projectName, "floor_plan").catch(() => [] as AssetRowWire[]),
    ]).then(([fp, rows]) => {
      if (cancelled) return;
      const planTypes = fp?.plan_types ?? null;
      setPlans(planTypes);
      setAssets(rows);
      if ((!planTypes || planTypes.length === 0) && rows.length === 0) {
        setStatus("empty");
      } else {
        setStatus("ready");
      }
    }).catch(() => {
      if (!cancelled) setStatus("error");
    });
    return () => {
      cancelled = true;
    };
  }, [projectName]);

  const planList = useMemo(() => plans ?? [], [plans]);

  // All floor_plan assets matching a given plan, in stable order. Most
  // plan_types resolve to 1 image; some carry 2–3 (different storey /
  // orientation variants of the same unit_type).
  const planImages = useMemo(
    () =>
      planList.map((plan) => {
        const shas = plan.source_image_sha256s ?? [];
        if (shas.length === 0) return [] as AssetRowWire[];
        const set = new Set(shas);
        return assets.filter((a) => set.has(a.asset_sha256));
      }),
    [planList, assets],
  );

  // Assets not referenced by any plan_type → surfaced as a trailing
  // "Other plans" bucket so we never drop ingested floor plans on the
  // floor. Suppressed once a bedrooms filter is active (bedrooms unknown
  // for these assets, so they can't satisfy a bedroom predicate).
  const otherAssets = useMemo(() => {
    const matched = new Set<string>();
    for (const pt of planList) for (const sha of pt.source_image_sha256s ?? []) matched.add(sha);
    return assets.filter((a) => !matched.has(a.asset_sha256));
  }, [planList, assets]);

  // Distinct bedroom counts that have at least one plan_type, sorted
  // ascending. Drives the chip row above the grid.
  const bedroomOptions = useMemo(() => {
    const counts = new Map<number, number>();
    for (const p of planList) {
      if (typeof p.bedrooms === "number") counts.set(p.bedrooms, (counts.get(p.bedrooms) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .sort(([a], [b]) => a - b)
      .map(([value, count]) => ({ value, count }));
  }, [planList]);

  // Visible plans (with original index preserved so the lightbox can
  // look up images via planImages[idx]).
  const visiblePlans = useMemo(() => {
    const all = planList.map((plan, idx) => ({ plan, idx }));
    if (selectedBedrooms.length === 0) return all;
    const beds = new Set(selectedBedrooms);
    return all.filter(({ plan }) => typeof plan.bedrooms === "number" && beds.has(plan.bedrooms));
  }, [planList, selectedBedrooms]);

  // Prune stale bedroom selections when the project changes (handled
  // by the fetch effect reset) or the plan list narrows.
  useEffect(() => {
    if (selectedBedrooms.length === 0) return;
    const valid = new Set(bedroomOptions.map((o) => o.value));
    const next = selectedBedrooms.filter((b) => valid.has(b));
    if (next.length !== selectedBedrooms.length) setSelectedBedrooms(next);
  }, [bedroomOptions, selectedBedrooms]);

  // Esc + arrow-key handler for the lightbox. Scoped to whichever
  // bucket is open: a plan_type's images (planIdx >= 0) or the "other"
  // bucket (planIdx === -1).
  useEffect(() => {
    if (!lightbox) return;
    const imgs = lightbox.planIdx === -1 ? otherAssets : planImages[lightbox.planIdx] ?? [];
    const total = imgs.length;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setLightbox(null);
      } else if (e.key === "ArrowLeft") {
        setLightbox((s) => (s && total > 0 ? { ...s, imgIdx: (s.imgIdx - 1 + total) % total } : s));
      } else if (e.key === "ArrowRight") {
        setLightbox((s) => (s && total > 0 ? { ...s, imgIdx: (s.imgIdx + 1) % total } : s));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox, planImages, otherAssets]);

  // Per-plan chip row (bedrooms / bathrooms / area / facing) — every
  // chip is omitted when its source field is missing or its value is
  // the "unknown" sentinel, so a sparse plan renders a shorter row
  // rather than placeholders.
  const chipsFor = (plan: PlanTypeWire): string[] => {
    const chips: string[] = [];
    if (typeof plan.bedrooms === "number" && plan.bedrooms > 0) chips.push(`${plan.bedrooms} BR`);
    else if (plan.bedrooms === 0) chips.push("Studio");
    if (typeof plan.bathrooms === "number" && plan.bathrooms > 0) chips.push(`${plan.bathrooms} BA`);
    if (typeof plan.total_area_sqm === "number" && plan.total_area_sqm > 0) {
      const a = Math.round(plan.total_area_sqm * 10) / 10;
      chips.push(`${a} sqm`);
    }
    if (typeof plan.balcony_sqm === "number" && plan.balcony_sqm > 0) {
      chips.push(`+${Math.round(plan.balcony_sqm * 10) / 10} sqm balcony`);
    }
    if (plan.facing && !isUnknown(plan.facing)) chips.push(plan.facing);
    return chips;
  };

  const bedroomLabel = (n: number) => (n === 0 ? "Studio" : `${n} BR`);

  if (status === "loading") {
    return <p style={{ fontFamily: "var(--font-serif)", fontSize: 14.5, fontStyle: "italic", color: "var(--ink-3)", margin: 0 }}>Loading unit plans…</p>;
  }
  if (status === "error") {
    return <p style={{ fontFamily: "var(--font-serif)", fontSize: 14.5, fontStyle: "italic", color: "var(--ink-3)", margin: 0 }}>Couldn't load unit plans right now. Please try again.</p>;
  }
  if (status === "empty") {
    return (
      <div style={{ maxWidth: 640 }}>
        <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "0 0 8px", letterSpacing: "-0.02em" }}>Unit plans</h2>
        <p style={{ fontFamily: "var(--font-serif)", fontSize: 14.5, fontStyle: "italic", color: "var(--ink-3)", margin: 0 }}>No floor plans for this project yet — typical for upcoming launches still being enriched.</p>
      </div>
    );
  }

  // Floor-plan "card" sub-renderer — used for both plan_type cards and
  // the trailing other-bucket cards. Click image or badge opens the
  // lightbox for that card's images.
  const card = (opts: {
    key: string;
    title: string;
    subtitle?: string;
    chips?: string[];
    images: AssetRowWire[];
    onOpen?: () => void;
  }) => {
    const main = opts.images[0];
    const extra = Math.max(0, opts.images.length - 1);
    return (
      <div key={opts.key} style={{ border: "1px solid var(--rule)", borderRadius: 10, overflow: "hidden", background: "var(--surface)", display: "flex", flexDirection: "column" }}>
        <button
          type="button"
          onClick={opts.images.length > 0 ? opts.onOpen : undefined}
          disabled={opts.images.length === 0}
          aria-label={opts.images.length > 0 ? `Open floor plan for ${opts.title}` : undefined}
          style={{ position: "relative", aspectRatio: "4 / 3", background: "var(--bg-2, #f4f3ef)", padding: 0, border: "none", display: "flex", alignItems: "center", justifyContent: "center", cursor: opts.images.length > 0 ? "zoom-in" : "default" }}
        >
          {main?.url ? (
            <img src={main.url} alt={`Floor plan for ${opts.title}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
          ) : (
            <span style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-4)" }}>No plan image</span>
          )}
          {extra > 0 && (
            <span style={{ position: "absolute", top: 8, right: 8, fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, color: "var(--ink)", background: "var(--surface)", padding: "3px 7px", borderRadius: 4, border: "1px solid var(--rule)", letterSpacing: "0.04em" }}>+{extra} more</span>
          )}
        </button>
        <div style={{ padding: "12px 14px 14px" }}>
          <div style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>{opts.title}</div>
          {opts.chips && opts.chips.length > 0 && (
            <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {opts.chips.map((c, j) => (
                <span key={j} style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-2)", background: "var(--accent-soft)", padding: "2px 7px", borderRadius: 4 }}>{c}</span>
              ))}
            </div>
          )}
          {opts.subtitle && (
            <div style={{ marginTop: 8, fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)", letterSpacing: "0.06em", textTransform: "uppercase" }}>{opts.subtitle}</div>
          )}
        </div>
      </div>
    );
  };

  const lightboxImgs = lightbox === null ? [] : lightbox.planIdx === -1 ? otherAssets : planImages[lightbox.planIdx] ?? [];
  const lightboxTitle = lightbox === null ? "" : lightbox.planIdx === -1 ? "Other plan" : planList[lightbox.planIdx]?.unit_type ?? "";
  const lightboxImg = lightbox === null ? undefined : lightboxImgs[lightbox.imgIdx];

  return (
    <div>
      <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 20, fontWeight: 500, color: "var(--ink)", margin: "0 0 6px", letterSpacing: "-0.02em" }}>Unit plans</h2>
      <p style={{ fontFamily: "var(--font-serif)", fontSize: 13.5, fontStyle: "italic", color: "var(--ink-3)", margin: "0 0 14px" }}>
        {planList.length > 0
          ? `Showing ${visiblePlans.length} of ${planList.length} plan type${planList.length === 1 ? "" : "s"} extracted from project floor plans.`
          : "Floor plans available — extracted unit metadata coming soon."}
      </p>

      {bedroomOptions.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
          {bedroomOptions.map(({ value, count }) => {
            const active = selectedBedrooms.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() =>
                  setSelectedBedrooms((s) => (s.includes(value) ? s.filter((v) => v !== value) : [...s, value]))
                }
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 11.5,
                  fontWeight: active ? 500 : 400,
                  color: active ? "var(--accent)" : "var(--ink-2)",
                  background: active ? "var(--accent-soft)" : "transparent",
                  border: "1px solid",
                  borderColor: active ? "var(--accent)" : "var(--rule)",
                  padding: "5px 11px",
                  borderRadius: 999,
                  cursor: "pointer",
                }}
              >
                {bedroomLabel(value)} <span style={{ color: "var(--ink-4)", marginLeft: 4 }}>{count}</span>
              </button>
            );
          })}
          {selectedBedrooms.length > 0 && (
            <button
              type="button"
              onClick={() => setSelectedBedrooms([])}
              style={{ fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", background: "transparent", border: "none", padding: "5px 8px", cursor: "pointer", textDecoration: "underline" }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 16 }}>
        {visiblePlans.map(({ plan, idx }) =>
          card({
            key: `${plan.unit_type}-${idx}`,
            title: plan.unit_type,
            subtitle: plan.classification && !isUnknown(plan.classification) ? plan.classification.replace(/_/g, " ") : undefined,
            chips: chipsFor(plan),
            images: planImages[idx],
            onOpen: () => setLightbox({ planIdx: idx, imgIdx: 0 }),
          }),
        )}
        {selectedBedrooms.length === 0 && otherAssets.length > 0 &&
          card({
            key: "__other__",
            title: "Other plans",
            subtitle: "Unit type not yet identified",
            images: otherAssets,
            onOpen: () => setLightbox({ planIdx: -1, imgIdx: 0 }),
          })}
      </div>

      {visiblePlans.length === 0 && (
        <p style={{ marginTop: 18, fontFamily: "var(--font-serif)", fontSize: 13.5, fontStyle: "italic", color: "var(--ink-3)" }}>No plans match this filter.</p>
      )}

      {lightbox !== null && lightboxImg && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Floor plan ${lightbox.imgIdx + 1} of ${lightboxImgs.length} for ${lightboxTitle}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) setLightbox(null);
          }}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Close floor plan"
            style={{ position: "absolute", top: 16, right: 16, background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", width: 34, height: 34, borderRadius: 34, fontSize: 22, lineHeight: 1, cursor: "pointer" }}
          >×</button>
          {lightboxImgs.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous"
                onClick={() => setLightbox((s) => (s && lightboxImgs.length > 0 ? { ...s, imgIdx: (s.imgIdx - 1 + lightboxImgs.length) % lightboxImgs.length } : s))}
                style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", width: 42, height: 42, borderRadius: 42, fontSize: 22, cursor: "pointer" }}
              >‹</button>
              <button
                type="button"
                aria-label="Next"
                onClick={() => setLightbox((s) => (s && lightboxImgs.length > 0 ? { ...s, imgIdx: (s.imgIdx + 1) % lightboxImgs.length } : s))}
                style={{ position: "absolute", right: 16, top: "50%", transform: "translateY(-50%)", background: "rgba(255,255,255,0.12)", border: "none", color: "#fff", width: 42, height: 42, borderRadius: 42, fontSize: 22, cursor: "pointer" }}
              >›</button>
            </>
          )}
          <div style={{ maxWidth: "min(1100px, 92vw)", maxHeight: "88vh", display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
            {lightboxImg.url && (
              <img
                src={lightboxImg.url}
                alt={`Floor plan for ${lightboxTitle}`}
                style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain", background: "#fff", borderRadius: 6 }}
              />
            )}
            <div style={{ color: "rgba(255,255,255,0.8)", fontFamily: "var(--font-ui)", fontSize: 12 }}>
              {lightboxTitle}{lightboxImgs.length > 1 ? ` · ${lightbox.imgIdx + 1} of ${lightboxImgs.length}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PropertyView({ projectName, onBack, onAsk }: { projectName: string | null; onBack: () => void; onAsk: (q: string) => void }) {
  const [tab, setTab] = useState<"overview" | "unitplans" | "location" | "costs">("overview");
  const [saved, setSaved] = useState(false);
  const [detail, setDetail] = useState<PropertyDetail | null>(null);
  // Gallery assets (project hero photos). Fetched in parallel with
  // details; the first row's URL drives the hero image. Empty array is
  // the legitimate "no listing source" answer.
  const [gallery, setGallery] = useState<AssetRowWire[]>([]);
  // "missing" covers a null name, a 404 (no_details / not_found — common
  // for pipeline projects), or a network failure. All degrade to the
  // same minimal card.
  const [status, setStatus] = useState<"loading" | "ready" | "missing">("loading");

  // Hydrate by name from /dataengine/projects/{name}/details — the same
  // endpoint the Workspace dossier uses. Card data is never trusted as a
  // pass-through; this is the authoritative fetch. Gallery fetch runs
  // in parallel and never blocks the page — a hero falls back to the
  // tone-coloured SVG if it 404s or comes back empty.
  useEffect(() => {
    if (!projectName) {
      setStatus("missing");
      setGallery([]);
      return;
    }
    let cancelled = false;
    setStatus("loading");
    setDetail(null);
    setGallery([]);
    Promise.all([
      getProjectDetails(projectName),
      listProjectAssets(projectName, "gallery").catch(() => [] as AssetRowWire[]),
    ])
      .then(([wire, rows]) => {
        if (cancelled) return;
        setGallery(rows);
        if (!wire) {
          setStatus("missing");
          return;
        }
        setDetail(adaptProjectDetails(wire));
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("missing");
      });
    return () => {
      cancelled = true;
    };
  }, [projectName]);

  const heroUrl = gallery[0]?.url;

  const tone: Tone = detail ? detail.tone : projectName ? toneFromName(projectName) : "forest";
  const headerTitle = detail?.name || projectName || "Project";
  const headerKicker = detail?.district || "Explore";

  // Shared chrome — back header + save/share — wraps every load state so
  // the page frame is stable while detail hydrates.
  const chrome = (body: ReactNode) => (
    <div className="pw-explore" style={mainStyle()}>
      <BackHeader
        kicker={headerKicker}
        title={headerTitle}
        onBack={onBack}
        backLabel="Back"
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => setSaved((s) => !s)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: saved ? "var(--accent-soft)" : "transparent", border: "1px solid", borderColor: saved ? "var(--accent)" : "var(--rule)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: saved ? "var(--accent)" : "var(--ink-2)" }}
            >
              <svg width="11" height="11" viewBox="0 0 14 14" fill={saved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.3">
                <path d="M3 2v11l4-3 4 3V2z" strokeLinejoin="round" />
              </svg>
              {saved ? "Saved" : "Save"}
            </button>
            <button style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--rule)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)" }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <path d="M4 4.5V2h9v9h-2.5M2 5h8v8H2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Share
            </button>
          </div>
        }
      />
      {body}
    </div>
  );

  if (status === "loading") {
    return chrome(
      <div className="pw-prop-wrap" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px 48px" }}>
        <PropertyHero tone={tone} imageUrl={heroUrl} alt={headerTitle} />
        <p style={{ marginTop: 24, fontFamily: "var(--font-serif)", fontSize: 15, fontStyle: "italic", color: "var(--ink-3)" }}>Loading project details…</p>
      </div>,
    );
  }

  if (status === "missing" || !detail) {
    return chrome(
      <div className="pw-prop-wrap" style={{ maxWidth: 720, margin: "0 auto", padding: "24px 32px 48px" }}>
        <PropertyHero tone={tone} imageUrl={heroUrl} alt={headerTitle} />
        <div style={{ marginTop: 24 }}>
          <h1 className="pw-prop-title" style={{ fontFamily: "var(--font-serif)", fontSize: 32, fontWeight: 500, margin: 0, letterSpacing: "-0.03em", color: "var(--ink)", lineHeight: 1.05 }}>{headerTitle}</h1>
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 15, fontStyle: "italic", color: "var(--ink-3)", margin: "12px 0 0", lineHeight: 1.6, maxWidth: 560 }}>
            We don't have a detailed profile for this project yet — it may be an upcoming launch still being enriched. Check back soon.
          </p>
        </div>
      </div>,
    );
  }

  // Up to four stat tiles, drawn from whichever URA-canonical fields the
  // project_details response actually carried.
  const stats = ([
    detail.district ? { label: "District", value: detail.district } : null,
    detail.tenure ? { label: "Tenure", value: detail.tenure } : null,
    detail.marketSegment ? { label: "Segment", value: detail.marketSegment } : null,
    detail.propertyTypes.length > 0 ? { label: "Type", value: detail.propertyTypes.join(", ") } : null,
    detail.topYear ? { label: "TOP", value: String(detail.topYear) } : null,
  ].filter(Boolean) as { label: string; value: string }[]).slice(0, 4);

  return chrome(
    <div className="pw-prop-wrap" style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 32px 48px" }}>
      <PropertyHero tone={tone} imageUrl={heroUrl} alt={detail.name} />

      <div className="pw-prop-grid" style={{ marginTop: 24, display: "grid", gridTemplateColumns: "1fr 360px", gap: 32 }}>
        <div>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 6, display: "flex", alignItems: "center", gap: 8 }}>
            {detail.category && <span style={{ padding: "2px 7px", borderRadius: 3, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)" }}>{detail.category}</span>}
            {detail.district && <span>{detail.district}</span>}
          </div>
          <h1 className="pw-prop-title" style={{ fontFamily: "var(--font-serif)", fontSize: 38, fontWeight: 500, margin: 0, letterSpacing: "-0.03em", color: "var(--ink)", lineHeight: 1.05 }}>{detail.name}</h1>
          {/* PROP-76 — modal header badge. Hidden when reviewCount === 0;
              the PropertyReviews section below renders its own zero-state. */}
          {detail.reviewCount > 0 && (
            <div style={{ marginTop: 10 }}>
              <ReviewBadge avg={detail.reviewAvg} count={detail.reviewCount} size="md" />
            </div>
          )}
          {(detail.category || detail.planningArea) && (
            <p style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontStyle: "italic", color: "var(--ink-2)", margin: "12px 0 0", lineHeight: 1.55, maxWidth: 580 }}>
              {[detail.category, detail.planningArea].filter(Boolean).join(" · ")}
            </p>
          )}

          {stats.length > 0 && (
            <div className="pw-prop-stats" style={{ marginTop: 28, padding: "20px 0", borderTop: "1px solid var(--rule)", borderBottom: "1px solid var(--rule)", display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 24 }}>
              {stats.map((s, i) => <PStat key={i} label={s.label} value={s.value} />)}
            </div>
          )}

          <div className="pw-prop-tabs" style={{ display: "flex", gap: 4, marginTop: 28, borderBottom: "1px solid var(--rule)" }}>
            {([
              ["overview", "Overview"],
              ["unitplans", "Unit plans"],
              ["location", "Location & nearby"],
              ["costs", "Cost breakdown"],
            ] as const).map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                style={{ background: "transparent", border: "none", padding: "10px 14px", cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 12, fontWeight: tab === k ? 500 : 400, color: tab === k ? "var(--accent)" : "var(--ink-3)", borderBottom: "2px solid", borderColor: tab === k ? "var(--accent)" : "transparent", marginBottom: -1 }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={{ paddingTop: 24 }}>
            {tab === "overview" && <OverviewTab detail={detail} />}
            {tab === "unitplans" && <UnitPlansTab projectName={detail.name} />}
            {tab === "location" && <LocationTab tone={tone} detail={detail} />}
            {tab === "costs" && <CostsTab projectName={detail.name} district={detail.district} />}
          </div>

          {/* Reviews — anonymous read, signed-in write (PROP-72 / PROP-75).
              onAggregateChange keeps the header ReviewBadge above in lockstep
              with the section's running aggregate, so a user who posts /
              edits / deletes a review sees the badge update without a refresh
              (PROP-76 / CLAUDE.md symmetry rule). */}
          <PropertyReviews
            projectName={detail.name}
            onAggregateChange={(avg, count) =>
              setDetail((d) => (d ? { ...d, reviewAvg: avg, reviewCount: count } : d))
            }
          />

          {/* PROP-82: user-driven data-quality feedback. Sits at the
              bottom of the detail surface — anchored low so it's not
              the first thing a curious buyer sees, but reachable
              when they spot something wrong. Slug-driven (the report
              writes to project_reports.project_slug). */}
          <div style={{ marginTop: 32 }}>
            <ProjectReportButton slug={detail.slug} />
          </div>
        </div>

          <aside>
            <div className="pw-prop-aside" style={{ position: "sticky", top: 80 }}>
              {/* Explore aside CTA (PROP-248, product-affirmed): refer the
                  visitor to a licensed agent via POST /leads/capture — the
                  lead lands unassigned in the pro-agent claim pool. This is
                  referral to an EXTERNAL licensed agent, deliberately distinct
                  from ProProp acting as an estate agency. context carries the
                  server-derived project identity so the claiming agent knows
                  what it's about. The fabricated named-agent block + dead CTAs
                  that used to sit here were removed in PROP-247; the
                  Ask-ProProp chips below remain the honest self-serve surface. */}
              <LeadCta
                variant="explore"
                source="explore_project_detail"
                context={`${detail.name} · ${detail.slug}`}
              />
              <div style={{ padding: 14, borderRadius: 10, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <svg width="13" height="13" viewBox="0 0 14 14">
                    <path d="M5 1 H2 a1 1 0 0 0 -1 1 V11 a1 1 0 0 0 1 1 H5" fill="none" stroke="var(--ink)" strokeWidth="1.4" strokeLinecap="round" />
                    <path d="M9 1 H12 a1 1 0 0 1 1 1 V11 a1 1 0 0 1 -1 1 H9" fill="none" stroke="var(--ai-accent)" strokeWidth="1.4" strokeLinecap="round" />
                  </svg>
                  <div style={{ fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ai-accent)" }}>Ask ProProp</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    `Is ${detail.name} a good fit for my budget?`,
                    `Compare ${detail.name} vs nearby resale`,
                    // PROP-249: project-aware like its siblings. Kept generic
                    // ("a unit") rather than hardcoding "3BR" — PropertyDetail
                    // carries no representative bedroom count, so naming one
                    // would imply a unit type the project may not offer.
                    `Total stamp duty on a unit at ${detail.name}`,
                  ].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => onAsk(q)}
                      style={{ background: "var(--surface)", border: "1px solid var(--ai-rule)", padding: "8px 11px", borderRadius: 6, textAlign: "left", fontFamily: "var(--font-serif)", fontSize: 12, fontStyle: "italic", color: "var(--ink-2)", cursor: "pointer", lineHeight: 1.35, transition: "background 150ms" }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#fff")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "var(--surface)")}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>,
  );
}

/* ═════════════════════════════════════════════════════════════
   SUB-VIEW: ARTICLE
   ═════════════════════════════════════════════════════════════ */
function ArticleHero({ kind }: { kind: Article["kind"] }) {
  const palette = {
    URA:    { fg: "#1d4a3a", bg: "#dfe7e0" },
    Policy: { fg: "#6b5320", bg: "#f0e7d0" },
    Market: { fg: "#2a4a66", bg: "#d9e3eb" },
    Brief:  { fg: "#3d4a45", bg: "#dde2e1" },
  }[kind];
  return (
    <div className="pw-article-hero" style={{ height: 280, borderRadius: 14, overflow: "hidden", background: palette.bg, position: "relative", border: "1px solid var(--rule)" }}>
      <svg viewBox="0 0 800 280" preserveAspectRatio="none" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        {kind === "URA" && (
          <g>
            <g stroke={palette.fg} strokeWidth="0.7" fill="none" opacity="0.4">
              {Array.from({ length: 20 }).map((_, i) => <line key={`v${i}`} x1={i * 40} y1="0" x2={i * 40} y2="280" />)}
              {Array.from({ length: 8 }).map((_, i) => <line key={`h${i}`} x1="0" y1={i * 40} x2="800" y2={i * 40} />)}
            </g>
            <rect x="180" y="80" width="100" height="160" fill={palette.fg} opacity="0.35" />
            <rect x="320" y="60" width="80"  height="180" fill={palette.fg} opacity="0.55" />
            <rect x="440" y="100" width="120" height="140" fill={palette.fg} opacity="0.4" />
            <rect x="600" y="120" width="60"  height="120" fill={palette.fg} opacity="0.5" />
            <circle cx="370" cy="150" r="8" fill={palette.fg} />
            <circle cx="370" cy="150" r="16" fill="none" stroke={palette.fg} strokeWidth="1.5" opacity="0.4" />
          </g>
        )}
        {kind === "Policy" && (
          <g>
            <rect x="120" y="60" width="560" height="160" fill="var(--surface)" stroke={palette.fg} strokeWidth="1.5" opacity="0.85" />
            <g fill={palette.fg} opacity="0.8">
              <rect x="150" y="80"  width="280" height="10" rx="2" />
              <rect x="150" y="105" width="500" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="115" width="480" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="125" width="500" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="135" width="420" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="155" width="440" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="165" width="380" height="3"  rx="1" opacity="0.5" />
              <rect x="150" y="175" width="460" height="3"  rx="1" opacity="0.5" />
            </g>
            <circle cx="620" cy="180" r="32" stroke={palette.fg} strokeWidth="2.5" fill="none" opacity="0.85" />
            <text x="620" y="186" textAnchor="middle" fontSize="14" fontFamily="serif" fontStyle="italic" fill={palette.fg} opacity="0.9">2026</text>
          </g>
        )}
        {kind === "Market" && (
          <g fill="none" stroke={palette.fg} strokeWidth="2.2" opacity="0.85">
            <polyline points="40,220 120,180 200,200 280,140 360,160 440,100 520,120 600,70 680,90 760,50" />
            {[[40,220],[120,180],[200,200],[280,140],[360,160],[440,100],[520,120],[600,70],[680,90],[760,50]].map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="3" fill={palette.fg} />
            ))}
            <line x1="20" y1="250" x2="780" y2="250" opacity="0.4" />
            <line x1="40" y1="30"  x2="40"  y2="260" opacity="0.4" />
          </g>
        )}
        {kind === "Brief" && (
          <g fill={palette.fg} opacity="0.8">
            <circle cx="320" cy="140" r="60" opacity="0.4" />
            <circle cx="430" cy="140" r="60" opacity="0.5" />
            <text x="375" y="156" textAnchor="middle" fontSize="40" fontFamily="serif" fontStyle="italic" opacity="0.95">brief</text>
          </g>
        )}
      </svg>
      <div style={{ position: "absolute", top: 14, left: 14, fontFamily: "var(--font-ui)", fontSize: 9.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", padding: "4px 9px", borderRadius: 3, background: "var(--surface)", color: palette.fg }}>{kind}</div>
    </div>
  );
}

function ArticleView({ article, onBack, onAsk }: { article: Article | null; onBack: () => void; onAsk: (q: string) => void }) {
  const a: Article = article || NEWS[0];
  const navigate = useNavigate();
  // PROP-192: derive the avatar initials from the real byline so house /
  // agent articles don't all show a hardcoded "JL".
  const initials =
    a.source.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "PW";

  return (
    <div className="pw-explore" style={mainStyle()}>
      <BackHeader
        kicker={a.kind}
        title="Article"
        onBack={onBack}
        backLabel="Back"
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <button style={{ background: "transparent", border: "1px solid var(--rule)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)", display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <path d="M3 2v11l4-3 4 3V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
              </svg>
              Save
            </button>
            <button style={{ background: "transparent", border: "1px solid var(--rule)", padding: "5px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-2)", display: "flex", alignItems: "center", gap: 6 }}>
              <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
                <path d="M4 4.5V2h9v9h-2.5M2 5h8v8H2z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Share
            </button>
          </div>
        }
      />

      <article className="pw-article-wrap" style={{ maxWidth: 880, margin: "0 auto", padding: "32px 32px 64px" }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--ai-accent)", marginBottom: 10 }}>{a.kind} · Long read</div>
        <h1 className="pw-article-title" style={{ fontFamily: "var(--font-serif)", fontSize: 44, fontWeight: 500, color: "var(--ink)", margin: 0, letterSpacing: "-0.035em", lineHeight: 1.05, maxWidth: 780 }}>{a.title}</h1>
        {a.dek && (
          <p className="pw-article-dek" style={{ fontFamily: "var(--font-serif)", fontSize: 19, fontStyle: "italic", color: "var(--ink-2)", margin: "18px 0 0", lineHeight: 1.45, maxWidth: 720 }}>{a.dek}</p>
        )}

        <div style={{ marginTop: 22, paddingBottom: 22, borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 36, background: "var(--accent-soft)", color: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 600 }}>{initials}</div>
          <div>
            {a.authorSlug ? (
              <button
                type="button"
                onClick={() => navigate(`/author/${a.authorSlug}`)}
                style={{ fontFamily: "var(--font-serif)", fontSize: 13.5, fontWeight: 500, color: "var(--ink)", background: "none", border: "none", padding: 0, cursor: "pointer", textAlign: "left", textDecoration: "underline", textUnderlineOffset: 2 }}
              >
                {a.source}
              </button>
            ) : (
              <div style={{ fontFamily: "var(--font-serif)", fontSize: 13.5, fontWeight: 500, color: "var(--ink)" }}>{a.source}</div>
            )}
            <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", marginTop: 2 }}>{a.date} · {a.readTime} read</div>
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <ArticleHero kind={a.kind} />
        </div>

        {/* PROP-184: render the REAL article body carried from the wire.
            The reader used to show one hardcoded URA Master Plan body for
            EVERY article. When body is absent (offline NEWS_FULL fixture)
            fall back to the dek rather than fabricating content. */}
        <div className="pw-article-body" style={{ marginTop: 32, maxWidth: 680 }}>
          {a.body && a.body.length > 0 ? (
            a.body.map((s, i) => <ArticleSection key={i} section={s} />)
          ) : (
            <p style={{ fontFamily: "var(--font-serif)", fontSize: 17, lineHeight: 1.6, color: "var(--ink-2)", margin: 0, fontStyle: "italic" }}>
              {a.dek || "The full article isn't available to preview here."}
            </p>
          )}
        </div>

        <div className="pw-article-cta" style={{ marginTop: 40, padding: "24px 28px", borderRadius: 12, background: "var(--ai-bg)", border: "1px solid var(--ai-rule)", display: "flex", alignItems: "center", gap: 18 }}>
          <svg width="40" height="40" viewBox="0 0 40 40" style={{ flexShrink: 0 }}>
            <rect width="40" height="40" rx="10" fill="var(--surface)" stroke="var(--ai-rule)" strokeWidth="1" />
            <path d="M15 10 H10 a2 2 0 0 0 -2 2 V28 a2 2 0 0 0 2 2 H15" fill="none" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" />
            <path d="M25 10 H30 a2 2 0 0 1 2 2 V28 a2 2 0 0 1 -2 2 H25" fill="none" stroke="var(--ai-accent)" strokeWidth="2.4" strokeLinecap="round" />
            <circle cx="20" cy="20" r="2.5" fill="var(--ai-accent)" />
          </svg>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.015em" }}>Have a question about this piece?</div>
            <div style={{ fontFamily: "var(--font-serif)", fontSize: 13, fontStyle: "italic", color: "var(--ink-2)", marginTop: 4 }}>Ask ProProp — answers will cite this article and the underlying URA data.</div>
          </div>
          <button
            onClick={() => onAsk(`What does the ${a.kind === "URA" ? "URA Draft Master Plan 2025" : a.title} mean for my search?`)}
            style={{ background: "var(--accent)", color: "var(--bubble-me-ink)", border: "none", padding: "10px 16px", borderRadius: 8, fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 500, cursor: "pointer", flexShrink: 0 }}
          >
            Start a thread
          </button>
        </div>
      </article>
    </div>
  );
}

// ArticleSection renders one structured body block (PROP-184), mapped by
// Section kind. Mirrors the styles the old hardcoded body used so the
// real article reads the same way the placeholder did.
function ArticleSection({ section }: { section: Section }) {
  switch (section.kind) {
    case "h2":
      return <h2 style={{ fontFamily: "var(--font-serif)", fontSize: 24, fontWeight: 500, color: "var(--ink)", margin: "32px 0 14px", letterSpacing: "-0.025em" }}>{section.text}</h2>;
    case "pull":
      return (
        <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: "3px solid var(--accent)", background: "var(--surface)", borderRadius: "0 8px 8px 0" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 22, fontStyle: "italic", fontWeight: 500, color: "var(--ink)", margin: 0, letterSpacing: "-0.015em", lineHeight: 1.35 }}>{section.text}</p>
        </blockquote>
      );
    case "callout":
      return (
        <div style={{ margin: "24px 0", padding: "16px 20px", borderRadius: 10, background: "var(--surface)", border: "1px solid var(--rule)" }}>
          <p style={{ fontFamily: "var(--font-serif)", fontSize: 16, lineHeight: 1.55, color: "var(--ink-2)", margin: 0 }}>{section.text}</p>
        </div>
      );
    case "sources":
      return (
        <div style={{ margin: "24px 0 0" }}>
          <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--ink-3)", marginBottom: 8 }}>Sources</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(section.items ?? []).map((it, i) => (
              <li key={i} style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink-3)", lineHeight: 1.6 }}>{it}</li>
            ))}
          </ul>
        </div>
      );
    case "image":
      return section.text ? <img src={section.text} alt="" style={{ width: "100%", borderRadius: 10, margin: "24px 0" }} /> : null;
    case "p":
    default:
      return <p style={{ fontFamily: "var(--font-serif)", fontSize: 17, lineHeight: 1.6, color: "var(--ink-2)", margin: "18px 0 0" }}>{section.text}</p>;
  }
}

/* ═════════════════════════════════════════════════════════════
   SHARED MAIN STYLE
   ═════════════════════════════════════════════════════════════ */
function mainStyle(): CSSProperties {
  return { flex: 1, height: "100%", overflow: "auto", background: "var(--bg)" };
}

/* ═════════════════════════════════════════════════════════════
   ROOT
   ═════════════════════════════════════════════════════════════ */
type View = "home" | "newsroom" | "launches" | "hot" | "property" | "article";

export function Explore() {
  useEffect(() => {
    const id = "pw-explore-mobile-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id;
    el.textContent = MOBILE_CSS;
    document.head.appendChild(el);
    return () => { document.getElementById(id)?.remove(); };
  }, []);

  const [view, setView] = useState<View>("home");
  const [selectedPropertyName, setSelectedPropertyName] = useState<string | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);

  // Buyer prefs (budget, areas, priorities, timeline) are owned by the
  // workspace surface — Explore reads from it for the Picked feed but
  // never writes back. Editing happens in PrefsEditModal.
  const { workspace } = useCurrentWorkspace();

  const back = () => { setView("home"); setSelectedPropertyName(null); setSelectedArticle(null); };
  // openProperty takes just the project name — the smallest disambiguating
  // handle. PropertyView hydrates the full record server-side from
  // /dataengine/projects/{name}/details rather than trusting passed-through
  // card data.
  const openProperty = (name: string) => { setSelectedPropertyName(name); setView("property"); };
  const openArticle  = (a: Article)        => { setSelectedArticle(a);  setView("article"); };

  // News feed used by the home-page Dispatches section. Falls back to
  // the 5-item NEWS seed when the API is off or the call hasn't
  // resolved yet, so first paint is never blank.
  const liveNews = useNewsFeed();
  // PROP-199: never pad real articles with hardcoded NEWS fixtures. Show
  // whatever real articles exist (offline demo still gets NEWS_FULL via
  // useNewsFeed); when there are none, the section below renders nothing
  // rather than fabricate a newsroom of fake headlines.
  const newsForHome: Article[] = liveNews.slice(0, 5);

  // New-launches feed for the home section. Empty until the pipeline
  // call resolves when VITE_EXPLORE_API is on; the LAUNCHES_FULL seed
  // when it's off.
  const launches = useLaunches();

  // Hottest projects (by 90d URA txn volume). Both the home strip and
  // the full chart consume the same array — the home strip slices the
  // top 5 so a single network call covers both surfaces.
  const hottest = useHottest(15);

  // Picked-for-you feed, scored against the workspace's saved prefs.
  // Re-keys on workspace id so a workspace edit repopulates.
  const picked = usePicked(workspace?.id);

  // Quick-prompts route to the AI Advisor with the query in the URL.
  const ask = (q: string) => {
    window.location.assign(`/chat/new?q=${encodeURIComponent(q)}`);
  };

  if (view === "newsroom") return <NewsroomView onBack={back} onOpenArticle={openArticle} />;
  if (view === "launches") return <LaunchesAllView onBack={back} onOpenProperty={openProperty} launches={launches} />;
  if (view === "hot")      return <HotChartView onBack={back} onOpenProperty={openProperty} hottest={hottest} />;
  if (view === "property") return <PropertyView projectName={selectedPropertyName} onBack={back} onAsk={ask} />;
  if (view === "article")  return <ArticleView article={selectedArticle} onBack={back} onAsk={ask} />;

  return (
    <div className="pw-explore" style={mainStyle()}>
      {/* Top bar — matches chat */}
      <div className="pw-h32" style={{ height: 48, flexShrink: 0, borderBottom: "1px solid var(--rule)", display: "flex", alignItems: "center", padding: "0 32px", gap: 12, background: "var(--bg)", position: "sticky", top: 0, zIndex: 5 }}>
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Today</div>
        <div style={{ fontFamily: "var(--font-serif)", fontSize: 14, fontWeight: 500, color: "var(--ink)", letterSpacing: "-0.01em" }}>Explore</div>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 6, border: "1px solid var(--rule)", background: "var(--surface)", fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
          <svg width="11" height="11" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.3" />
            <path d="M9 9l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
          </svg>
          Search projects, districts, schools…
        </div>
      </div>

      <ExploreHero onAsk={ask} />

      {/* NEW LAUNCHES */}
      <section className="pw-h32 pw-section-head" style={{ padding: "32px 32px 8px", maxWidth: 1200, margin: "0 auto" }}>
        <SectionHead kicker="Just listed" title="New launches this week" action={`See all ${launches.length}`} onAction={() => setView("launches")} />
        <div className="pw-grid-launches" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {launches.slice(0, 3).map((p, i) => <NewLaunchCard key={i} p={p} onOpen={openProperty} />)}
        </div>
      </section>

      {/* HOTTEST + PICKED */}
      <section className="pw-h32 pw-grid-hotpicked pw-section-head" style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 28 }}>
        <div>
          <SectionHead kicker="Trending" title="Hottest right now" action="Full chart" onAction={() => setView("hot")} />
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {hottest.slice(0, 5).map((p, i) => <HotRow key={i} rank={i + 1} p={p} onOpen={openProperty} />)}
            {hottest.length === 0 && (
              <div style={{ padding: "20px 14px", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--ink-3)" }}>
                Loading the latest 90-day transaction volume…
              </div>
            )}
          </div>
        </div>
        <div>
          <SectionHead kicker="Picked for you" title="Based on your filters & chats" />
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {picked.items.map((p, i) => <PickedCard key={i} p={p} onOpen={openProperty} />)}
            {picked.items.length === 0 && (
              <div style={{ padding: "20px 14px", fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: 13, color: "var(--ink-3)" }}>
                {picked.loading
                  ? "Scoring projects against your prefs…"
                  : picked.reason === "no_prefs"
                    ? "Set a budget and area in your workspace to see picks tailored to you."
                    : picked.reason === "no_workspace"
                      ? "Sign in to see picks tailored to your prefs."
                      : "No matches yet — try broadening your area or budget in your workspace."}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* NEWS — only when real articles exist; never fabricated (PROP-199) */}
      {newsForHome.length > 0 && (
        <section className="pw-h32 pw-section-head" style={{ padding: "12px 32px 48px", maxWidth: 1200, margin: "0 auto", borderTop: "1px solid var(--rule)", marginTop: 8, paddingTop: 32 }}>
          <SectionHead kicker="Stay informed" title="URA, policy & market dispatches" action="Newsroom" onAction={() => setView("newsroom")} />
          <div className="pw-grid-news" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 16 }}>
            <NewsCard a={newsForHome[0]} large onOpen={openArticle} />
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {newsForHome.slice(1, 3).map((a, i) => <NewsCard key={i} a={a} onOpen={openArticle} />)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {newsForHome.slice(3, 5).map((a, i) => <NewsCard key={i} a={a} onOpen={openArticle} />)}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
